import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { requireAdminOrDeny } from "@/lib/admin-guard";
import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { secureSignOut } from "@/lib/secure-sign-out";
import { toast } from "sonner";
import {
  useRecurringAudit,
  RecurringSummaryCards,
  RecurringCell,
  NextChargeCell,
} from "@/components/admin/RecurringBilling";
import {
  getAdminOverview,
  listUsers,
  setUserTier,
  deleteUser,
  listPosts,
  deletePost,
  getRecentActivity,
} from "@/lib/admin.functions";
import { logLogoutEvent, listLogoutEvents } from "@/lib/logout-events.functions";
import {
  listCompliance,
  updateCompliance,
  listBugs,
  createBug,
  updateBug,
  deleteBug,
  listLaunchTasks,
  createLaunchTask,
  updateLaunchTask,
  deleteLaunchTask,
} from "@/lib/admin-ops.functions";
import {
  getMemberStats,
  getConversionFunnel,
  listMembers,
  updateMember,
  exportMemberData,
  listFeedback,
  createFeedback,
  updateFeedback,
  deleteFeedback,
  listIncidents,
  createIncident,
  updateIncident,
  deleteIncident,
  auditPaymentProviders,
} from "@/lib/admin-extra.functions";
import { sendMemberActionEmail } from "@/lib/admin-member-actions.functions";
import { manuallyVerifyEmail } from "@/lib/admin-verify-email.functions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Shield,
  Sparkles,
  Users,
  CreditCard,
  FileText,
  Activity,
  MessageSquare,
  Calendar,
  BarChart3,
  Search,
  Trash2,
  ArrowUpCircle,
  ArrowDownCircle,
  Mail,
  Download,
  LogOut,
  Loader2,
  ClipboardCheck,
  Bug,
  ListTodo,
  Plus,
  UserCircle,
  MessageCircle,
  ShieldAlert,
  Wallet,
  Bell,
  ShoppingBag,
} from "lucide-react";
import { listSubscribers } from "@/lib/subscribers.functions";
import { NewsletterPanel } from "@/components/admin/NewsletterPanel";
import { FunnelReportPanel } from "@/components/admin/FunnelReportPanel";
import { OctoberOfferPanel } from "@/components/admin/OctoberOfferPanel";
import { UnpaidDropoffPanel } from "@/components/admin/UnpaidDropoffPanel";
import { MemberJourneyPanel } from "@/components/admin/MemberJourneyPanel";
import { getGrowthMetrics } from "@/lib/growth.functions";
import { getLaunchDashboard, getLeadSources } from "@/lib/launch-dashboard.functions";
import { TrendingUp, FileSpreadsheet, Rocket, Upload } from "lucide-react";
import { ExportButtons } from "@/components/admin/ExportButtons";

import { exportDashboardReport } from "@/lib/admin-export";
import { CostsTab } from "@/components/admin/CostsTab";
import { RevenueTab } from "@/components/admin/RevenueTab";
import { FinanceTab } from "@/components/admin/finance/FinanceTab";
import { BusinessKpiTab } from "@/components/admin/business/BusinessKpiTab";
import { RetentionSweepPanel } from "@/components/admin/RetentionSweepPanel";
import { FounderComebackPanel } from "@/components/admin/FounderComebackPanel";
import { MemberReengagementPanel } from "@/components/admin/MemberReengagementPanel";

import { ModerationTab } from "@/components/admin/ModerationTab";
import { ShopOrdersTab } from "@/components/admin/ShopOrdersTab";
import { PaymentsRenewalsTab } from "@/components/admin/PaymentsRenewalsTab";
import { PaymentMonitoringPanel } from "@/components/admin/PaymentMonitoringPanel";
import { LegacyPriceHoldsPanel } from "@/components/admin/LegacyPriceHoldsPanel";
import { EmailSmokeTestPanel } from "@/components/admin/EmailSmokeTestPanel";
import { PlatformStatsPanel } from "@/components/admin/PlatformStatsPanel";
import { LiveSessionSurveyPanel } from "@/components/admin/LiveSessionSurveyPanel";
import { VouchersPanel } from "@/components/admin/VouchersPanel";
import { WebsiteAnalyticsPanel } from "@/components/admin/WebsiteAnalyticsPanel";
import { EngagementPanel } from "@/components/admin/EngagementPanel";
import { SocialMediaTrackerPanel } from "@/components/admin/SocialMediaTrackerPanel";
import { DashboardTotalsPanel } from "@/components/admin/DashboardTotalsPanel";
import { MembershipTruthPanel } from "@/components/admin/MembershipTruthPanel";
import { UnpaidAccountsBanner } from "@/components/admin/UnpaidAccountsBanner";
import { AccountStatusBadge, resolveAccountStatus } from "@/components/AccountStatusBadge";
import { Button } from "@/components/ui/button";

import { TestUserCleanupCard } from "@/components/admin/TestUserCleanupCard";
import { EventsAdminPanel } from "@/components/admin/EventsAdminPanel";
import { ToolkitManagerPanel } from "@/components/admin/ToolkitManagerPanel";

import { DollarSign, ShieldCheck, MailCheck, Tag } from "lucide-react";


/** Safely parse timestamps that may use a short "+00" offset (invalid on Safari/iOS). */
function parseTimestamp(value: unknown): Date | null {
  if (!value) return null;
  const raw = String(value).trim();
  const normalised = raw
    .replace(" ", "T")
    .replace(/([+-]\d{2})$/, "$1:00");
  const d = new Date(normalised);
  return Number.isNaN(d.getTime()) ? null : d;
}

const ADMIN_TABS = [
  "finance",
  "kpis",
  "overview",
  "launch-dashboard",
  "users",
  "leads",
  "newsletter",
  "funnel",
  "growth",
  "membership",
  "content",
  "toolkit",
  "community",
  "events",
  "live-sessions",
  "vouchers",
  "analytics",
  "compliance",
  "bugs",
  "launch",
  "members",
  "feedback",
  "safety",
  "costs",
  "revenue",
  "moderation",
  "email-tests",
  "payment-monitoring",
  "waitlist",
  "courses",
  "shop-orders",
  "payments-renewals",
] as const;

export const MEMBERSHIP_TYPE_FILTERS = [
  "all",
  "paid",
  "complimentary",
  "free",
  "therapist",
  "admin",
] as const;
export type MembershipTypeFilter =
  | (typeof MEMBERSHIP_TYPE_FILTERS)[number]
  | "free";

const MEMBERSHIP_TYPE_LABEL: Record<MembershipTypeFilter, string> = {
  all: "All",
  paid: "Paid subscription",
  complimentary: "Complimentary",
  free: "Free account — payment required",
  therapist: "Therapist",
  admin: "Admin",
};

export const Route = createFileRoute("/admin")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin Dashboard — Recovery Trauma™" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: (typeof ADMIN_TABS)[number]; mtype?: MembershipTypeFilter } => {
    const raw = typeof search.tab === "string" ? search.tab : "users";
    // Aliases for header deep-links that don't have their own tab yet.
    const alias: Record<string, (typeof ADMIN_TABS)[number]> = {
      waitlist: "leads",
      courses: "content",
    };
    const resolved = alias[raw] ?? raw;
    const tab = (ADMIN_TABS as readonly string[]).includes(resolved)
      ? (resolved as (typeof ADMIN_TABS)[number])
      : "users";
    const rawType = typeof search.mtype === "string" ? search.mtype : "all";
    const mtype = (MEMBERSHIP_TYPE_FILTERS as readonly string[]).includes(rawType)
      ? (rawType as MembershipTypeFilter)
      : "all";
    return { tab, mtype };
  },
  beforeLoad: requireAdminOrDeny,
  component: AdminDashboardGate,
});

function AdminDashboardGate() {
  const ctx = Route.useRouteContext() as { granted?: boolean; email?: string | null };
  if (ctx?.granted === false) {
    return <AccessDenied email={ctx.email ?? null} />;
  }
  return <AdminDashboard />;
}

function AccessDenied({ email }: { email: string | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handleSignOut = async () => {
    try {
      await secureSignOut(queryClient);
    } finally {
      navigate({ to: "/admin-login", replace: true });
    }
  };
  return (
    <div className="min-h-dvh flex items-center justify-center px-6 py-12 bg-background">
      <div
        role="alert"
        aria-labelledby="access-denied-title"
        className="max-w-md w-full rounded-2xl border border-border bg-card shadow-sm p-8 text-center"
      >
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <ShieldAlert className="h-7 w-7 text-destructive" aria-hidden="true" />
        </div>
        <h1 id="access-denied-title" className="text-2xl font-semibold text-foreground">
          Access denied
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Your account doesn’t have permission to view the admin dashboard.
          {email ? (
            <>
              {" "}You’re signed in as <span className="font-medium text-foreground">{email}</span>.
            </>
          ) : null}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          If you believe this is a mistake, contact a Recovery Trauma™ administrator
          to request access.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition"
          >
            Go to home
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition"
          >
            Sign out & switch account
          </button>
        </div>
      </div>
    </div>
  );
}

type Tab =
  | "finance"
  | "kpis"
  | "overview"
  | "launch-dashboard"
  | "users"
  | "leads"
  | "newsletter"
  | "funnel"
  | "growth"
  | "membership"
  | "content"
  | "toolkit"
  | "community"
  | "events"
  | "live-sessions"
  | "vouchers"
  | "analytics"
  | "compliance"
  | "bugs"
  | "launch"
  | "members"
  | "feedback"
  | "safety"
  | "costs"
  | "revenue"
  | "moderation"
  | "email-tests"
  | "payment-monitoring"
  | "shop-orders"
  | "payments-renewals";

type NavItem =
  | { kind: "tab"; id: Tab; label: string; icon: typeof Shield }
  | { kind: "link"; to: string; label: string; icon: typeof Shield };

type NavGroup = { id: string; label: string; icon: typeof Shield; items: NavItem[] };

const OWNER_ONLY_TABS: string[] = ["kpis", "finance", "costs"];

const NAV_GROUPS: NavGroup[] = [
  {
    id: "overview",
    label: "Overview",
    icon: BarChart3,
    items: [
      { kind: "tab", id: "overview", label: "Dashboard", icon: BarChart3 },
      { kind: "tab", id: "analytics", label: "Analytics", icon: BarChart3 },
      { kind: "tab", id: "growth", label: "Growth", icon: TrendingUp },
    ],
  },
  {
    id: "launch",
    label: "Launch",
    icon: Rocket,
    items: [
      { kind: "tab", id: "launch-dashboard", label: "Launch Dashboard", icon: Rocket },
      { kind: "link", to: "/admin/launch-readiness", label: "Launch Readiness", icon: Rocket },
      { kind: "link", to: "/admin/health-monitor", label: "Health Monitor", icon: Activity },
      { kind: "link", to: "/admin/board-cards", label: "Recovery Board Cards", icon: Activity },
      { kind: "tab", id: "launch", label: "Launch Tasks", icon: ListTodo },
      { kind: "link", to: "/admin/issues", label: "Issues & Bug Reports", icon: Bug },
      { kind: "tab", id: "bugs", label: "Bugs", icon: Bug },
      { kind: "tab", id: "feedback", label: "Feedback & Reports", icon: MessageCircle },
    ],
  },
  {
    id: "members",
    label: "Members",
    icon: UserCircle,
    items: [
      { kind: "tab", id: "users", label: "Users", icon: Users },
      { kind: "tab", id: "members", label: "Members", icon: UserCircle },
      { kind: "tab", id: "vouchers", label: "Vouchers", icon: Tag },
      { kind: "tab", id: "membership", label: "Memberships", icon: CreditCard },
      { kind: "tab", id: "leads", label: "Leads", icon: Mail },
      { kind: "tab", id: "newsletter", label: "Newsletter", icon: Mail },
      { kind: "tab", id: "funnel", label: "Funnel", icon: Mail },
    ],
  },
  {
    id: "therapists",
    label: "Therapists",
    icon: Shield,
    items: [
      { kind: "link", to: "/admin/therapist-applications", label: "Therapist Applications", icon: Shield },
      { kind: "link", to: "/admin/therapist-directory", label: "Therapist Directory", icon: Users },
      { kind: "link", to: "/admin/therapist-events", label: "Therapist Events", icon: Calendar },
    ],
  },
  {
    id: "content",
    label: "Content",
    icon: FileText,
    items: [
      { kind: "tab", id: "content", label: "Library", icon: FileText },
      { kind: "tab", id: "toolkit", label: "Toolkit", icon: Activity },
      { kind: "tab", id: "events", label: "Events", icon: Calendar },
      { kind: "tab", id: "live-sessions", label: "Live Sessions Survey", icon: Calendar },
      { kind: "link", to: "/admin-testimonials", label: "Testimonials & Feedback", icon: MessageSquare },
    ],
  },
  {
    id: "community-safety",
    label: "Community & Safety",
    icon: ShieldAlert,
    items: [
      { kind: "tab", id: "community", label: "Community", icon: MessageSquare },
      { kind: "tab", id: "moderation", label: "Moderation", icon: ShieldCheck },
      { kind: "link", to: "/admin/support-inbox", label: "Support Inbox", icon: Mail },
      { kind: "link", to: "/admin/crisis-alerts", label: "Crisis Alerts", icon: Shield },
      { kind: "tab", id: "safety", label: "Safety & Incidents", icon: ShieldAlert },
      { kind: "link", to: "/admin/escalation-cases", label: "Escalation Cases", icon: ShieldAlert },
      { kind: "link", to: "/admin/escalation-pathway", label: "Escalation Pathway", icon: ShieldAlert },
    ],
  },
  {
    id: "business",
    label: "Business",
    icon: Wallet,
    items: [
      { kind: "tab", id: "kpis", label: "Business KPIs", icon: TrendingUp },
      { kind: "tab", id: "finance", label: "Finance", icon: Wallet },
      { kind: "tab", id: "revenue", label: "Revenue", icon: DollarSign },
      { kind: "tab", id: "costs", label: "Costs & Finance", icon: Wallet },
      { kind: "tab", id: "payment-monitoring", label: "Payment Monitoring", icon: ShieldAlert },
      { kind: "tab", id: "shop-orders", label: "Shop Orders", icon: ShoppingBag },
      { kind: "tab", id: "payments-renewals", label: "Payments & Renewals", icon: CreditCard },
      { kind: "link", to: "/admin/invoices", label: "HMRC Invoices", icon: FileText },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: ClipboardCheck,
    items: [
      { kind: "link", to: "/admin/team-roles", label: "Team Roles", icon: Users },
      { kind: "link", to: "/admin/contracts", label: "Contracts", icon: FileText },
      { kind: "link", to: "/admin/access-audit", label: "Access Audit", icon: Shield },
      { kind: "link", to: "/admin/notifications", label: "Notifications", icon: Bell },
      { kind: "tab", id: "compliance", label: "Compliance", icon: ClipboardCheck },
      { kind: "link", to: "/admin/broadcasts", label: "Email Broadcasts", icon: Mail },
      { kind: "link", to: "/admin/halloween-tracking", label: "Mansion Tracking", icon: Activity },
      { kind: "tab", id: "email-tests", label: "Email Tests", icon: MailCheck },
      { kind: "link", to: "/admin/ownership-portability", label: "Ownership & Portability", icon: FileText },
      { kind: "link", to: "/admin/library", label: "Library Admin", icon: FileText },
      { kind: "link", to: "/admin/sanctuary", label: "Sanctuary Seasons & Stats", icon: Sparkles },
      { kind: "link", to: "/admin/waitlist", label: "Waitlist", icon: Mail },
      { kind: "link", to: "/admin/audio-health", label: "Audio Health", icon: Activity },
      { kind: "link", to: "/admin/confirm-test-user", label: "Confirm Test User", icon: Shield },
    ],
  },
];

function AdminDashboard() {
  const { tab = "users" } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setTab = (next: Tab) =>
    navigate({ to: "/admin", search: (prev: any) => ({ ...prev, tab: next }), replace: true });
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);


  const [isFinanceOwner, setIsFinanceOwner] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data } = await supabase
        .from("finance_owners" as never)
        .select("user_id")
        .eq("user_id", u.user.id)
        .maybeSingle();
      if (alive) setIsFinanceOwner(!!data);
    })();
    return () => {
      alive = false;
    };
  }, []);
  const navGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (it) => isFinanceOwner || !(it.kind === "tab" && OWNER_ONLY_TABS.includes(it.id)),
    ),
  }));

  const q = query.trim().toLowerCase();
  const searchResults = q
    ? navGroups.flatMap((g) =>
        g.items
          .filter(
            (it) =>
              it.label.toLowerCase().includes(q) ||
              g.label.toLowerCase().includes(q),
          )
          .map((it) => ({ group: g, item: it })),
      ).slice(0, 12)
    : [];

  const logEvent = useServerFn(logLogoutEvent);

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    setLogoutError("");
    const ua = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : undefined;

    // Log attempt while we still have an authenticated session.
    try {
      await logEvent({ data: { status: "attempt", userAgent: ua } });
    } catch (e) {
      console.error("Failed to log logout attempt", e);
    }

    // Log success BEFORE signOut() — otherwise the bearer token is gone
    // and requireSupabaseAuth rejects the RPC.
    try {
      await logEvent({ data: { status: "success", userAgent: ua } });
    } catch (e) {
      console.error("Failed to log logout success", e);
    }

    try {
      const { error } = await secureSignOut(queryClient);
      if (error) throw error;
      navigate({ to: "/admin-login", replace: true });
    } catch (err: any) {
      const msg = err?.message || "Sign-out failed. Please try again.";
      // signOut failed — session is still valid, so we can still log the failure.
      try {
        await logEvent({ data: { status: "failure", errorMessage: msg, userAgent: ua } });
      } catch (e) {
        console.error("Failed to log logout failure", e);
      }
      setLogoutError(msg);
      toast.error(msg);
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:max-w-[1400px] lg:px-8 xl:max-w-screen-2xl xl:px-10">

      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-sm rounded-2xl border border-gold/30 bg-card p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <h2 className="font-display text-lg text-foreground">Log out?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              You will be signed out of the admin dashboard.
            </p>
            {logoutError && (
              <p className="mt-3 rounded-lg bg-destructive/10 p-2.5 text-xs text-destructive">
                {logoutError}
              </p>
            )}
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => { setShowLogoutModal(false); setLogoutError(""); }}
                disabled={isLoggingOut}
                className="flex-1 rounded-xl border border-gold/20 bg-card py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-gold/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                disabled={isLoggingOut}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-destructive py-2.5 text-sm font-medium text-white transition-colors hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoggingOut && <Loader2 className="h-4 w-4 animate-spin" />}
                {isLoggingOut ? "Signing out..." : logoutError ? "Try again" : "Log out"}
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="mb-4 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
            <Shield className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-2xl text-foreground sm:text-3xl">Admin Dashboard</h1>
            <p className="hidden text-sm text-muted-foreground sm:block">
              Manage members, content, and the wellbeing of your community.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowLogoutModal(true)}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gold/20 bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-gold/10"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Log out</span>
        </button>
      </header>

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
          placeholder="Jump to section… (users, revenue, crisis, email tests)"
          className="w-full rounded-xl border border-gold/20 bg-card py-2.5 ps-9 pe-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-gold/50 focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); setSearchOpen(false); }}
            className="absolute end-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-gold/10 hover:text-foreground"
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
        {searchOpen && q && (
          <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-gold/20 bg-card shadow-lg">
            {searchResults.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">No matches for "{query}"</p>
            ) : (
              <ul className="max-h-80 overflow-y-auto py-1">
                {searchResults.map(({ group, item }) => {
                  const key = item.kind === "tab" ? `t-${item.id}` : `l-${item.to}`;
                  const inner = (
                    <>
                      <item.icon className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
                      <span className="flex-1 truncate">{item.label}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{group.label}</span>
                    </>
                  );
                  const cls = "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground hover:bg-gold/10";
                  if (item.kind === "tab") {
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setTab(item.id); setQuery(""); setSearchOpen(false); }}
                          className={cls}
                        >
                          {inner}
                        </button>
                      </li>
                    );
                  }
                  return (
                    <li key={key}>
                      <Link
                        to={item.to}
                        onClick={() => { setQuery(""); setSearchOpen(false); }}
                        className={cls}
                      >
                        {inner}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>



      <div className="lg:flex lg:items-start lg:gap-8">
        {/* Desktop / tablet sidebar (lg+) — same items as the mobile accordion */}
        <aside className="hidden lg:block lg:w-64 lg:shrink-0 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:pe-2">
          <div className="space-y-2">
            {navGroups.map((group) => {
              const isActiveGroup = group.items.some(
                (it) => it.kind === "tab" && it.id === tab,
              );
              return (
                <div key={`sb-${group.id}`} className="rounded-2xl border border-gold/20 bg-card">
                  <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b border-gold/10">
                    <group.icon className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
                    <span className="truncate">{group.label}</span>
                  </div>
                  <ul className="flex flex-col gap-0.5 p-2">
                    {group.items.map((item) => {
                      const isActive = item.kind === "tab" && item.id === tab;
                      const cls = `flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-gold text-white shadow-sm shadow-gold/30"
                          : isActiveGroup
                          ? "text-foreground hover:bg-gold/10"
                          : "text-foreground/80 hover:bg-gold/10"
                      }`;
                      if (item.kind === "tab") {
                        return (
                          <li key={`sb-${item.id}`}>
                            <button onClick={() => setTab(item.id)} className={cls}>
                              <item.icon className="h-4 w-4 shrink-0" />
                              <span className="truncate">{item.label}</span>
                            </button>

                          </li>
                        );
                      }
                      return (
                        <li key={`sb-${item.to}`}>
                          <Link to={item.to} className={cls}>
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span className="truncate">{item.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {/* Mobile / small-tablet accordion (hidden at lg+) */}
          <nav className="mb-6 space-y-2 lg:hidden">
            {navGroups.map((group) => {
              const isActiveGroup = group.items.some(
                (it) => it.kind === "tab" && it.id === tab,
              );
              return (
                <details
                  key={group.id}
                  open={isActiveGroup}
                  className="group rounded-2xl border border-gold/20 bg-card"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-foreground hover:bg-gold/5">
                    <span className="flex min-w-0 items-center gap-2">
                      <group.icon className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
                      <span className="truncate">{group.label}</span>
                    </span>
                    <span className="text-xs text-muted-foreground transition-transform group-open:rotate-180">▾</span>
                  </summary>
                  <div className="flex flex-wrap gap-1.5 border-t border-gold/10 p-2">
                    {group.items.map((item) => {
                      const isActive = item.kind === "tab" && item.id === tab;
                      const className = `inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-gold text-white shadow-sm shadow-gold/30"
                          : "text-foreground/80 hover:bg-gold/10"
                      }`;
                      if (item.kind === "tab") {
                        return (
                          <button
                            key={item.id}
                            onClick={() => setTab(item.id)}
                            className={className}
                          >
                            <item.icon className="h-4 w-4" />
                            {item.label}
                          </button>

                        );
                      }
                      return (
                        <Link key={item.to} to={item.to} className={className}>
                          <item.icon className="h-4 w-4" />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                </details>
              );
            })}
          </nav>

          {tab === "overview" && <OverviewTab />}
          {tab === "launch-dashboard" && <LaunchDashboardTab />}
          {tab === "users" && <UsersTab />}
          {tab === "leads" && <LeadsTab />}
          {tab === "newsletter" && <NewsletterPanel />}
          {tab === "funnel" && (
            <div className="space-y-10">
              <OctoberOfferPanel />
              <FunnelReportPanel />
              <UnpaidDropoffPanel />
              <MemberJourneyPanel />
            </div>
          )}
          {tab === "growth" && <GrowthTab />}
          {tab === "membership" && <MembershipTab />}
          {tab === "content" && <ContentTab />}
          {tab === "toolkit" && <ToolkitTab />}
          {tab === "community" && <CommunityTab />}
          {tab === "events" && <EventsTab />}
          {tab === "live-sessions" && <LiveSessionSurveyPanel />}
          {tab === "vouchers" && <VouchersPanel />}
          {tab === "analytics" && <AnalyticsTab />}
          {tab === "compliance" && <ComplianceTab />}
          {tab === "bugs" && <BugsTab />}
          {tab === "launch" && <LaunchTasksTab />}
          {tab === "members" && <MembersTab />}
          {tab === "feedback" && <FeedbackTab />}
          {tab === "safety" && <SafetyTab />}
          {tab === "kpis" && isFinanceOwner && <BusinessKpiTab />}
          {tab === "finance" && isFinanceOwner && <FinanceTab />}
          {tab === "costs" && isFinanceOwner && <CostsTab />}
          {OWNER_ONLY_TABS.includes(tab) && !isFinanceOwner && (
            <p className="text-sm text-muted-foreground">This section is only available to the owner.</p>
          )}
          {tab === "revenue" && (
            <div className="space-y-6">
              {isFinanceOwner && <RevenueTab />}
              <LegacyPriceHoldsPanel />
              <RetentionSweepPanel />
              <FounderComebackPanel />
              <MemberReengagementPanel />
            </div>
          )}
          {tab === "moderation" && <ModerationTab />}
          {tab === "email-tests" && <EmailSmokeTestPanel />}
          {tab === "payment-monitoring" && <PaymentMonitoringPanel />}
          {tab === "shop-orders" && <ShopOrdersTab />}
          {tab === "payments-renewals" && <PaymentsRenewalsTab />}
        </div>
      </div>

    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  description,
  to,
  search,
  title,
}: {
  label: string;
  value: number | string;
  hint?: string;
  description?: string;
  to?: string;
  search?: Record<string, unknown>;
  title?: string;
}) {
  const body = (
    <div
      title={title ?? description}
      className={
        "rounded-2xl border border-gold/20 bg-card p-5 " +
        (to ? "cursor-pointer transition hover:bg-gold/10 hover:border-gold/40" : "")
      }
    >
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-3xl text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      {description && (
        <div className="mt-2 text-[11px] leading-snug text-muted-foreground/80">
          {description}
        </div>
      )}
    </div>
  );
  if (to) {
    return (
      <Link to={to as any} search={(search ?? {}) as any} className="block no-underline">
        {body}
      </Link>
    );
  }
  return body;
}


function OverviewTab() {
  const fetcher = useServerFn(getAdminOverview);
  const bugsFetcher = useServerFn(listBugs);
  const complianceFetcher = useServerFn(listCompliance);
  const tasksFetcher = useServerFn(listLaunchTasks);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => fetcher(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });


  if (isLoading) return <div className="text-muted-foreground">Loading overview…</div>;
  if (!data) return null;

  const handleDashboardReport = async () => {
    try {
      const [bugs, compliance, tasks] = await Promise.all([
        bugsFetcher().catch(() => ({ bugs: [] as any[] })),
        complianceFetcher().catch(() => ({ items: [] as any[] })),
        tasksFetcher().catch(() => ({ tasks: [] as any[] })),
      ]);
      const bugList = (bugs?.bugs ?? []) as any[];
      const compItems = (compliance?.items ?? []) as any[];
      const taskList = (tasks?.tasks ?? []) as any[];
      const bugBy = (k: string) => bugList.filter((b) => b.status === k).length;
      const sevBy = (k: string) => bugList.filter((b) => b.severity === k).length;
      const compBy = (k: string) => compItems.filter((c) => c.status === k).length;
      const taskBy = (k: string) => taskList.filter((t) => t.status === k).length;
      const totalTasks = taskList.length || 1;
      const readiness = Math.round((taskBy("done") / totalTasks) * 100);

      await exportDashboardReport({
        users: {
          "Total Registered Accounts": data.stats.totalRegisteredAccounts,
          "Founding Members": data.stats.foundingMembers,
          "Registered · Not Yet Paid": data.stats.registeredNotPaid,
        },
        membership: {
          "Founding Members": data.stats.foundingMembers,
          "Registered · Not Yet Paid": data.stats.registeredNotPaid,
          "Total Registered Accounts": data.stats.totalRegisteredAccounts,
        },

        community: {
          "Posts": data.stats.totalPosts,
          "Comments": data.stats.totalComments,
          "Journal Entries": data.stats.totalJournalEntries,
          "Mood Check-ins": data.stats.totalMoodChecks,
          "Exercise Sessions": data.stats.totalExerciseSessions,
        },
        revenue: { Status: "Payment provider not yet connected" },
        bugs: {
          "Total": bugList.length,
          "Open": bugBy("open"),
          "In Progress": bugBy("in_progress"),
          "Fixed": bugBy("fixed"),
          "Retest Needed": bugBy("retest_needed"),
          "Closed": bugBy("closed"),
          "Severity — Blocker": sevBy("blocker"),
          "Severity — High": sevBy("high"),
          "Severity — Medium": sevBy("medium"),
          "Severity — Low": sevBy("low"),
        },
        compliance: {
          "Total Items": compItems.length,
          "Approved": compBy("approved"),
          "In Review": compBy("in_review"),
          "Draft": compBy("draft"),
          "Needs Update": compBy("needs_update"),
        },
        launch: {
          "Total Tasks": taskList.length,
          "Done": taskBy("done"),
          "In Progress": taskBy("in_progress"),
          "Todo": taskBy("todo"),
          "Blocked": taskBy("blocked"),
          "Launch Readiness %": `${readiness}%`,
        },
      });
      toast.success("Dashboard report downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    }
  };

  return (
    <div className="space-y-6">
      <UnpaidAccountsBanner count={data.stats.registeredNotPaid ?? 0} />

      <MembershipTruthPanel />
      <PlatformStatsPanel />
      <DashboardTotalsPanel />
      <SocialMediaTrackerPanel />
      <TestUserCleanupCard />




      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl text-foreground">Platform overview</h2>
        <button
          onClick={handleDashboardReport}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gold/90"
        >
          <FileSpreadsheet className="h-4 w-4" /> Export Dashboard Report
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <StatCard
          label="Members"
          value={data.stats.foundingMembers}
          description="Paid, active memberships."
          to="/admin/members"
        />
        <StatCard
          label="Cancelled"
          value={data.stats.cancelledMembers ?? 0}
          description="No longer members."
          to="/admin/members"
        />
        <StatCard
          label="Ending Soon"
          value={data.stats.endingSoonMembers ?? 0}
          description="Auto-renewal off — access ends at the period end."
          to="/admin/members"
        />
      </div>



      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Conversion
        </h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <StatCard
            label="Registered Accounts"
            value={data.stats.totalRegisteredAccounts}
          />
          <StatCard label="Paid Members" value={data.stats.foundingMembers} />
          <StatCard
            label="Payment Conversion Rate"
            value={`${data.stats.paymentConversionRate ?? 0}%`}
            description="Paid Members ÷ Registered Accounts."
          />
          <StatCard
            label="Awaiting Payment"
            value={data.stats.registeredNotPaid}
            description="Registered accounts with no completed payment."
          />
          <StatCard
            label="Email Verification Rate"
            value={`${data.stats.emailVerificationRate ?? 0}%`}
            description="Accounts that have confirmed their email."
          />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Exercise Sessions" value={data.stats.totalExerciseSessions} />
          <StatCard label="Community Posts" value={data.stats.totalPosts} />
        </div>
      </div>






      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/admin/query-log"
          className="rounded-2xl border border-gold/20 bg-card p-4 text-sm font-semibold text-foreground hover:bg-gold/10">
          🔎 What people are asking →
        </Link>
        <Link to="/admin/questions"
          className="rounded-2xl border border-gold/20 bg-card p-4 text-sm font-semibold text-foreground hover:bg-gold/10">
          ❓ Ask Recovery Trauma™ →
        </Link>
        <Link to="/admin-testimonials"
          className="rounded-2xl border border-gold/20 bg-card p-4 text-sm font-semibold text-foreground hover:bg-gold/10">
          💬 Testimonials & feedback →
        </Link>
        <Link to="/admin/waitlist" search={{} as any}
          className="rounded-2xl border border-gold/20 bg-card p-4 text-sm font-semibold text-foreground hover:bg-gold/10">
          💛 Waitlist signups →
        </Link>
        <Link to="/admin/lead-conversion" search={{} as any}
          className="rounded-2xl border border-gold/20 bg-card p-4 text-sm font-semibold text-foreground hover:bg-gold/10">
          📈 Lead conversion tracking →
        </Link>
        <Link to="/admin/library"
          className="rounded-2xl border border-gold/20 bg-card p-4 text-sm font-semibold text-foreground hover:bg-gold/10">
          📚 Library documents →
        </Link>
      </div>


      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        <div className="rounded-2xl border border-gold/20 bg-card p-6">
          <h2 className="font-display text-lg text-foreground">Recent sign-ups</h2>
          <div className="mt-4 divide-y divide-border/40">
            {data.recentUsers.slice(0, 10).map((u: any) => (
              <div key={u.user_id} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-foreground">{u.display_name ?? "Unnamed"}</span>
                <span className="text-xs text-muted-foreground">
                  {u.membership_tier} · {new Date(u.joined_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-gold/20 bg-card p-6">
          <h2 className="font-display text-lg text-foreground">Most-used exercises</h2>
          <div className="mt-4 space-y-2">
            {data.popular.length === 0 && (
              <p className="text-sm text-muted-foreground">No sessions yet.</p>
            )}
            {data.popular.map((p: { slug: string; count: number }) => (
              <div key={p.slug} className="flex items-center justify-between text-sm">
                <span className="text-foreground capitalize">{p.slug.replace(/-/g, " ")}</span>
                <span className="text-xs text-muted-foreground">{p.count} sessions</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <RecentActivityCard />

      <LeadSourcesCard />

      <LogoutEventsCard />
    </div>
  );
}

function LeadSourcesCard() {
  const fetcher = useServerFn(getLeadSources);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "lead-sources"],
    queryFn: () => fetcher(),
    refetchInterval: 60_000,
  });

  const totalSignups = (data?.signupSources ?? []).reduce((s, r) => s + r.count, 0);
  const totalTraffic = (data?.trafficSources ?? []).reduce((s, r) => s + r.count, 0);
  const pct = (n: number, d: number) => (d ? Math.round((n / d) * 1000) / 10 : 0);

  const labelize = (s: string) =>
    s
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg text-foreground">📈 Where leads are coming from</h2>
        <span className="text-xs text-muted-foreground">Last 30 days · traffic</span>
      </div>

      {isLoading || !data ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading lead sources…</p>
      ) : (
        <div className="mt-4 grid gap-6 md:grid-cols-3">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-foreground">
              Waitlist signups by source
            </h3>
            {data.signupSources.length === 0 ? (
              <p className="text-sm text-muted-foreground">No signups yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.signupSources.map((r) => (
                  <li key={r.label} className="flex items-center justify-between">
                    <span className="truncate text-foreground">{labelize(r.label)}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {r.count} · {pct(r.count, totalSignups)}%
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-foreground">
              Website traffic by channel
            </h3>
            {data.trafficSources.length === 0 ? (
              <p className="text-sm text-muted-foreground">No traffic recorded.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.trafficSources.map((r) => (
                  <li key={r.label} className="flex items-center justify-between">
                    <span className="truncate text-foreground">{r.label}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {r.count} · {pct(r.count, totalTraffic)}%
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-foreground">Top referring sites</h3>
            {data.topReferrers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No referrers yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.topReferrers.map((r) => (
                  <li key={r.host} className="flex items-center justify-between gap-3">
                    <span className="truncate text-foreground">{r.host}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{r.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {data && data.recentLeads.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-semibold text-foreground">Most recent leads</h3>
          <div className="overflow-hidden rounded-xl border border-gold/15">
            <table className="w-full text-sm">
              <thead className="bg-gold/5 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-start">Email</th>
                  <th className="px-3 py-2 text-start">Source</th>
                  <th className="px-3 py-2 text-start">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold/10">
                {data.recentLeads.slice(0, 10).map((l) => (
                  <tr key={l.email + l.created_at}>
                    <td className="px-3 py-2 text-foreground">{l.email}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {l.source ? labelize(l.source) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(l.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RecentActivityCard() {
  const fetcher = useServerFn(getRecentActivity);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin", "recent-activity"],
    queryFn: () => fetcher(),
  });

  const kindStyles: Record<string, { label: string; cls: string }> = {
    journal: { label: "Journal", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400" },
    mood: { label: "Mood", cls: "bg-pink-500/15 text-pink-600 dark:text-pink-400" },
    session: { label: "Exercise", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
    post: { label: "Post", cls: "bg-violet-500/15 text-violet-600 dark:text-violet-400" },
    comment: { label: "Comment", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
    signup: { label: "Sign-up", cls: "bg-gold/20 text-[var(--gold-deep)]" },
  };

  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg text-foreground">Recent changes</h2>
          <p className="text-xs text-muted-foreground">
            Latest activity across journals, moods, exercises, community, and sign-ups.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="rounded-full border border-gold/20 px-3 py-1 text-xs font-semibold text-foreground/70 hover:bg-gold/10 disabled:opacity-50"
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : (data?.activity ?? []).length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <div className="mt-4 divide-y divide-border/40 max-h-[28rem] overflow-y-auto">
          {data!.activity.map((e: any) => {
            const style = kindStyles[e.kind] ?? { label: e.kind, cls: "bg-muted text-muted-foreground" };
            return (
              <div key={e.id} className="flex items-start justify-between gap-3 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${style.cls}`}>
                      {style.label}
                    </span>
                    <Link
                      to="/admin/users/$userId"
                      params={{ userId: e.user_id }}
                      className="truncate text-foreground hover:underline"
                    >
                      {e.display_name ?? "Unnamed user"}
                    </Link>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground" title={e.summary}>
                    {e.summary}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LogoutEventsCard() {
  const fetcher = useServerFn(listLogoutEvents);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin", "logout-events"],
    queryFn: () => fetcher(),
  });

  const statusStyles: Record<string, string> = {
    attempt: "bg-muted text-muted-foreground",
    success: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    failure: "bg-destructive/15 text-destructive",
  };

  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg text-foreground">Admin sign-out activity</h2>
          <p className="text-xs text-muted-foreground">
            Last 50 sign-out attempts, successes, and failures.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="rounded-full border border-gold/20 px-3 py-1 text-xs font-semibold text-foreground/70 hover:bg-gold/10 disabled:opacity-50"
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : (data?.events ?? []).length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No sign-out events yet.</p>
      ) : (
        <div className="mt-4 divide-y divide-border/40">
          {data!.events.map((e: any) => (
            <div key={e.id} className="flex items-start justify-between gap-3 py-2.5 text-sm">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusStyles[e.status] ?? ""}`}
                  >
                    {e.status}
                  </span>
                  <span className="text-foreground">{e.display_name ?? "Unknown admin"}</span>
                </div>
                {e.error_message && (
                  <p className="mt-1 truncate text-xs text-destructive" title={e.error_message}>
                    {e.error_message}
                  </p>
                )}
                {e.user_agent && (
                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground" title={e.user_agent}>
                    {e.user_agent}
                  </p>
                )}
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {new Date(e.created_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [search, setSearch] = useState("");
  const [tier, setTierFilter] = useState<"all" | "free" | "healing">("all");
  const list = useServerFn(listUsers);
  const tierFn = useServerFn(setUserTier);
  const delFn = useServerFn(deleteUser);
  const qc = useQueryClient();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null));
  }, []);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "users", search, tier],
    queryFn: () => list({ data: { search, tier } }),
  });

  const tierMut = useMutation({
    mutationFn: (vars: { userId: string; tier: "free" | "healing" }) =>
      tierFn({ data: vars }),
    onSuccess: () => {
      refetch();
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    },
  });

  const delMut = useMutation({
    mutationFn: (vars: { userId: string }) => delFn({ data: vars }),
    onSuccess: () => {
      refetch();
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
      toast.success("User deleted");
    },
    onError: (e: any) => toast.error(e?.message || "Delete failed"),
  });

  const reminderFn = useServerFn(sendMemberActionEmail);
  const reminderMut = useMutation({
    mutationFn: (vars: {
      userId: string;
      action: "resend_activation" | "resend_verification";
    }) => reminderFn({ data: vars }),
    onSuccess: () => toast.success("Payment reminder sent"),
    onError: (e: any) => toast.error(e?.message || "Couldn't send reminder"),
  });



  const { data: audit } = useRecurringAudit();
  const auditByUser = new Map(
    (audit?.rows ?? []).map((r) => [r.user_id, r]),
  );

  return (
    <div className="space-y-4">
      <RecurringSummaryCards />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="w-full rounded-full border border-gold/20 bg-card py-2 ps-9 pe-4 text-sm text-foreground outline-none focus:border-gold/60"
          />
        </div>
        <div className="flex gap-1 rounded-full border border-gold/20 bg-card p-1">
          {(["all", "free", "healing"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTierFilter(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${
                tier === t ? "bg-gold text-white" : "text-foreground/70 hover:bg-gold/10"
              }`}
            >
              {t === "healing" ? "Paid Members" : t === "free" ? "Payment Required" : "All"}
            </button>
          ))}
        </div>
        <div className="ms-auto">
          <ExportButtons
            rows={(data?.users ?? []) as any[]}
            columns={[
              { key: "display_name", header: "Name" },
              { key: "user_id", header: "User ID" },
              { key: "membership_tier", header: "Tier" },
              {
                key: "recurring",
                header: "Recurring",
                get: (r: any) => auditByUser.get(r.user_id)?.kind ?? "unknown",
              },
              {
                key: "recurring_provider",
                header: "Provider",
                get: (r: any) => auditByUser.get(r.user_id)?.provider ?? "",
              },
              {
                key: "next_charge",
                header: "Next Charge",
                get: (r: any) => auditByUser.get(r.user_id)?.next_charge_at ?? "",
              },
              {
                key: "access_until",
                header: "Access Until",
                get: (r: any) => auditByUser.get(r.user_id)?.access_until ?? "",
              },
              { key: "joined_at", header: "Joined", get: (r: any) => r.joined_at ? new Date(r.joined_at).toISOString() : "" },
            ]}
            filename="recovery-trauma-users"
            sheetName="Users"
            filters={{ search, tier }}
          />
        </div>
      </div>


      <div className="rounded-2xl border border-gold/20 bg-card">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading users…</div>
        ) : (
          <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">

            <thead className="bg-gold/10 text-start text-xs uppercase tracking-wider text-foreground/70">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Recurring</th>
                <th className="px-4 py-3">Next Charge</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.users ?? []).map((u: any) => (
                <tr key={u.user_id} className="border-t border-border/30">
                  <td className="px-4 py-3 text-foreground">
                    <Link
                      to="/admin/users/$userId"
                      params={{ userId: u.user_id }}
                      className="text-[var(--gold-deep)] hover:underline"
                    >
                      {u.display_name ?? "Unnamed"}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <AccountStatusBadge
                      status={resolveAccountStatus({
                        paidAccessActive: false,
                        membershipStatus: u.membership_status ?? null,
                      })}
                    />
                  </td>

                  <td className="px-4 py-3">
                    <RecurringCell row={auditByUser.get(u.user_id)} />
                  </td>
                  <td className="px-4 py-3">
                    <NextChargeCell row={auditByUser.get(u.user_id)} />
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(u.joined_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      {u.membership_tier === "free" ? (
                        <>
                          <a
                            href="/membership"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-500/25 dark:text-amber-200"
                          >
                            Complete Membership
                          </a>
                          <button
                            onClick={() => {
                              if (
                                confirm(
                                  `Send a payment reminder to ${u.display_name ?? "this user"}?`,
                                )
                              ) {
                                reminderMut.mutate({
                                  userId: u.user_id,
                                  action: "resend_activation",
                                });
                              }
                            }}
                            disabled={reminderMut.isPending}
                            className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-3 py-1.5 text-xs font-semibold text-[var(--gold-deep)] hover:bg-gold/25 disabled:opacity-50"
                          >
                            Send Payment Reminder
                          </button>
                          <button
                            onClick={() => {
                              const link = `${window.location.origin}/membership`;
                              navigator.clipboard
                                ?.writeText(link)
                                .then(() => toast.success("Payment link copied"))
                                .catch(() => toast.error("Couldn't copy link"));
                            }}
                            className="inline-flex items-center gap-1 rounded-full border border-gold/30 px-3 py-1.5 text-xs font-semibold text-foreground/70 hover:bg-gold/10"
                          >
                            Copy Payment Link
                          </button>
                          <button
                            onClick={() =>
                              tierMut.mutate({ userId: u.user_id, tier: "healing" })
                            }
                            className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-3 py-1.5 text-xs font-semibold text-[var(--gold-deep)] hover:bg-gold/25"
                          >
                            <ArrowUpCircle className="h-3.5 w-3.5" /> Make Member
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() =>
                            tierMut.mutate({ userId: u.user_id, tier: "free" })
                          }
                          className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1.5 text-xs font-semibold text-foreground/70 hover:bg-muted/80"
                        >
                          <ArrowDownCircle className="h-3.5 w-3.5" /> Downgrade
                        </button>
                      )}
                      <button
                        disabled={u.user_id === currentUserId}
                        onClick={() => {
                          if (u.user_id === currentUserId) {
                            toast.error("You can't delete your own account");
                            return;
                          }
                          if (confirm(`Delete ${u.display_name ?? "this user"}?`))
                            delMut.mutate({ userId: u.user_id });
                        }}
                        title={u.user_id === currentUserId ? "You can't delete your own account" : "Delete user"}
                        className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/20 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>

                    </div>
                  </td>

                </tr>
              ))}
              {(data?.users ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                    No users match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

        )}
      </div>
    </div>
  );
}

function MembershipTab() {
  const fetcher = useServerFn(getAdminOverview);
  const { data } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => fetcher(),
    // Refresh every 30s so the launch KPIs are effectively live.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const s = data?.stats;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          label="Founding Members"
          value={s?.foundingMembers ?? "—"}
          description="Active paid subscriptions. Primary launch KPI."
          to="/admin/members"
        />
        <StatCard
          label="Registered · Not Yet Paid"
          value={s?.registeredNotPaid ?? "—"}
          description="Account exists but no active paid membership."
          to="/admin/members"
        />
        <StatCard
          label="Total Registered Accounts"
          value={s?.totalRegisteredAccounts ?? "—"}
          description="Founding Members + Registered (Not Yet Paid). Excludes anonymous visitors."
          to="/admin/members"
        />
      </div>

      <MembershipTruthPanel />

      <div className="rounded-2xl border border-gold/20 bg-card p-6">
        <h2 className="font-display text-lg text-foreground">Payments</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Payment provider isn't connected yet. Once payments are enabled, expired
          memberships and payment status will appear here.
        </p>
      </div>
    </div>
  );
}

function PlaceholderCard({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: string[];
}) {
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-6">
      <h2 className="font-display text-lg text-foreground">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {items.map((i) => (
          <li
            key={i}
            className="rounded-xl border border-dashed border-gold/30 bg-background px-3 py-2 text-sm text-foreground/80"
          >
            {i}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContentTab() {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold-light/40 to-cream p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-[var(--gradient-gold)] p-2.5 text-foreground shadow-[var(--shadow-gold)]">
            <FileText className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg text-foreground">Library — Documents</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload worksheets, PDFs, guides, and images into the Recovery Trauma™ Library.
              Assign category (e.g. <em>Understanding Trauma</em>, <em>Nervous System</em>),
              tags, and visibility (public, members, or Founding Members only).
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/admin/library"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[var(--gradient-gold)] px-5 py-2.5 text-sm font-semibold tracking-wide text-foreground shadow-[var(--shadow-gold)] transition hover:opacity-90"
              >
                <Upload className="h-4 w-4" strokeWidth={2} /> Upload worksheet or document
              </Link>
              <Link
                to="/admin/library"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-gold/40 bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-gold-light/30"
              >
                Manage library documents
              </Link>
            </div>
          </div>
        </div>
      </div>

      <PlaceholderCard
        title="Articles & Courses"
        description="Long-form articles and courses are edited in code today. A visual editor can be added in a follow-up."
        items={["Create Articles", "Edit Articles", "Delete Articles", "Manage Courses"]}
      />
    </div>
  );
}


function ToolkitTab() {
  return <ToolkitManagerPanel />;
}

function CommunityTab() {
  const list = useServerFn(listPosts);
  const del = useServerFn(deletePost);
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "posts"],
    queryFn: () => list(),
  });
  const delMut = useMutation({
    mutationFn: (vars: { postId: string }) => del({ data: vars }),
    onSuccess: () => {
      refetch();
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg text-foreground">Community Posts</h2>
        <ExportButtons
          rows={(data?.posts ?? []) as any[]}
          columns={[
            { key: "id", header: "Post ID" },
            { key: "topic", header: "Topic" },
            { key: "author_name", header: "Author" },
            { key: "content", header: "Content" },
            { key: "created_at", header: "Created", get: (r: any) => new Date(r.created_at).toISOString() },
          ]}
          filename="recovery-trauma-community-posts"
          sheetName="Community"
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-2">
          {(data?.posts ?? []).map((p: any) => (
            <div
              key={p.id}
              className="flex items-start justify-between gap-4 rounded-2xl border border-gold/20 bg-card p-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="rounded-full bg-gold/15 px-2 py-0.5 font-semibold text-[var(--gold-deep)]">
                    {p.topic}
                  </span>
                  <span>{p.author_name}</span>
                  <span>·</span>
                  <span>{new Date(p.created_at).toLocaleString()}</span>
                </div>
                <p className="mt-2 line-clamp-3 text-sm text-foreground">{p.content}</p>
              </div>
              <button
                onClick={() => {
                  if (confirm("Remove this post?")) delMut.mutate({ postId: p.id });
                }}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/10 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/20"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          ))}
          {(data?.posts ?? []).length === 0 && (
            <div className="rounded-2xl border border-dashed border-gold/30 bg-card p-6 text-center text-sm text-muted-foreground">
              No posts yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EventsTab() {
  return <EventsAdminPanel />;
}


function AnalyticsTab() {
  const fetcher = useServerFn(getAdminOverview);
  const { data } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => fetcher(),
  });
  return (
    <div className="space-y-6">
      {/* Primary live + conversion + revenue dashboard */}
      <WebsiteAnalyticsPanel />

      {/* Who actually uses the platform */}
      <EngagementPanel />

      {/* Platform usage summary */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          label="Founding Members"
          value={data?.stats.foundingMembers ?? "—"}
          description="Active paid subscriptions."
        />
        <StatCard
          label="Registered · Not Yet Paid"
          value={data?.stats.registeredNotPaid ?? "—"}
          description="Signed up, no active paid membership."
        />
        <StatCard
          label="Total Registered Accounts"
          value={data?.stats.totalRegisteredAccounts ?? "—"}
          description="Paid + unpaid registered accounts."
        />

        <StatCard
          label="Sessions"
          value={data?.stats.totalExerciseSessions ?? "—"}
        />
      </div>

      <div className="rounded-2xl border border-gold/20 bg-card p-6">
        <h2 className="font-display text-lg text-foreground">Most popular resources</h2>
        <div className="mt-4 space-y-2">
          {(data?.popular ?? []).map((p: { slug: string; count: number }) => (
            <div key={p.slug} className="flex items-center justify-between text-sm">
              <span className="text-foreground capitalize">
                {p.slug.replace(/-/g, " ")}
              </span>
              <span className="text-xs text-muted-foreground">{p.count} sessions</span>
            </div>
          ))}
          {(data?.popular ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">No session data yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function GrowthTab() {
  const fetcher = useServerFn(getGrowthMetrics);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "growth"],
    queryFn: () => fetcher(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading growth metrics…
      </div>
    );
  }

  const w = data?.waitlist;
  const c = data?.conversion;
  const r30 = data?.retention30;
  const r90 = data?.retention90;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl text-foreground">Growth & Retention</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Track waitlist signups, conversion to paying members, and how members stick around.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-5">
        <StatCard label="Waitlist Signups" value={w?.total ?? "—"} />
        <StatCard label="Signups (Last 30d)" value={w?.last30 ?? "—"} />
        <StatCard label="Paying Members" value={c?.members ?? "—"} />
        <StatCard
          label="Conversion Rate"
          value={c ? `${c.rate}%` : "—"}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <RetentionCard
          title="30-Day Retention"
          subtitle="Members who joined ≥30 days ago and were active in the last 30 days"
          retained={r30?.retained ?? 0}
          cohort={r30?.cohortSize ?? 0}
          rate={r30?.rate ?? 0}
        />
        <RetentionCard
          title="90-Day Retention"
          subtitle="Members who joined ≥90 days ago and were active in the last 90 days"
          retained={r90?.retained ?? 0}
          cohort={r90?.cohortSize ?? 0}
          rate={r90?.rate ?? 0}
        />
      </div>

      <div className="rounded-2xl border border-gold/20 bg-card p-6 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">How these are calculated</p>
        <ul className="mt-2 list-disc space-y-1 ps-5">
          <li><strong>Waitlist signups</strong>: total entries in the email subscribers list.</li>
          <li><strong>Conversion rate</strong>: paying members ÷ waitlist signups.</li>
          <li><strong>Retention</strong>: a member counts as retained if they logged a journal entry, mood check, or exercise session within the retention window.</li>
        </ul>
      </div>
    </div>
  );
}

function RetentionCard({
  title,
  subtitle,
  retained,
  cohort,
  rate,
}: {
  title: string;
  subtitle: string;
  retained: number;
  cohort: number;
  rate: number;
}) {
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-6">
      <h3 className="font-display text-lg text-foreground">{title}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-3xl text-foreground">{rate}%</span>
        <span className="text-xs text-muted-foreground">
          {retained} of {cohort} members
        </span>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-[var(--gold-deep)] transition-all"
          style={{ width: `${Math.min(100, rate)}%` }}
        />
      </div>
      {cohort === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          No eligible members in this cohort yet.
        </p>
      )}
    </div>
  );
}


function LeadsTab() {
  const [search, setSearch] = useState("");
  const [accountType, setAccountType] = useState<"all" | "guest" | "free" | "paid">("all");
  const [days, setDays] = useState<"all" | "7" | "30" | "90">("all");
  const fetcher = useServerFn(listSubscribers);

  const since =
    days === "all"
      ? undefined
      : new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000).toISOString();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "leads", search, accountType, days],
    queryFn: () => fetcher({ data: { search, accountType, since } }),
  });

  const subs = data?.subscribers ?? [];
  const totals = data?.totals ?? { total: 0, guest: 0, free: 0, paid: 0 };




  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-5">
        <StatCard label="Total Subscribers" value={totals.total} />
        <StatCard label="Free Subscribers" value={totals.guest} />
        <StatCard label="Free Accounts (Payment Required)" value={totals.free} />
        <StatCard label="Paid Members" value={totals.paid} />
      </div>

      <div className="rounded-2xl border border-gold/20 bg-card p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search emails…"
                className="w-full rounded-full border border-gold/30 bg-background py-2 ps-9 pe-3 text-sm focus:border-gold focus:outline-none"
              />
            </div>
            <select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value as any)}
              className="rounded-full border border-gold/30 bg-background px-3 py-2 text-sm"
            >
              <option value="all">All accounts</option>
              <option value="guest">Free subscribers</option>
              <option value="free">Free Accounts (Payment Required)</option>
              <option value="paid">Paid members</option>
            </select>
            <select
              value={days}
              onChange={(e) => setDays(e.target.value as any)}
              className="rounded-full border border-gold/30 bg-background px-3 py-2 text-sm"
            >
              <option value="all">All time</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
          <ExportButtons
            rows={subs as any[]}
            columns={[
              { key: "email", header: "Email" },
              { key: "account_type", header: "Account Type" },
              { key: "membership_tier", header: "Membership Tier" },
              { key: "source", header: "Source" },
              { key: "marketing_consent", header: "Marketing Consent", get: (r: any) => (r.marketing_consent ? "yes" : "no") },
              { key: "created_at", header: "Signup Date", get: (r: any) => new Date(r.created_at).toISOString() },
              { key: "last_activity_at", header: "Last Activity", get: (r: any) => new Date(r.last_activity_at).toISOString() },
            ]}
            filename="recovery-trauma-leads"
            sheetName="Leads"
            filters={{ search, accountType, days }}
          />

        </div>

        <div className="mt-5 overflow-x-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : subs.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No subscribers match these filters yet.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/40 text-start text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pe-3">Email</th>
                  <th className="py-2 pe-3">Account</th>
                  <th className="py-2 pe-3">Source</th>
                  <th className="py-2 pe-3">Consent</th>
                  <th className="py-2 pe-3">Signup</th>
                  <th className="py-2 pe-3">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s: any) => (
                  <tr key={s.id} className="border-b border-border/30">
                    <td className="py-2 pe-3 text-foreground">{s.email}</td>
                    <td className="py-2 pe-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          s.account_type === "paid"
                            ? "bg-gold/20 text-[var(--gold-deep)]"
                            : s.account_type === "free"
                              ? "bg-blush/40 text-foreground"
                              : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {s.account_type}
                      </span>
                    </td>
                    <td className="py-2 pe-3 text-muted-foreground">{s.source}</td>
                    <td className="py-2 pe-3 text-muted-foreground">
                      {s.marketing_consent ? "Yes" : "No"}
                    </td>
                    <td className="py-2 pe-3 text-muted-foreground">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 pe-3 text-muted-foreground">
                      {new Date(s.last_activity_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// ============= Compliance Tab =============

const COMPLIANCE_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "in_review", label: "In Review" },
  { value: "approved", label: "Approved" },
  { value: "needs_update", label: "Needs Update" },
] as const;

function statusBadgeClass(status: string) {
  switch (status) {
    case "approved":
    case "done":
    case "closed":
      return "bg-green-100 text-green-800 border-green-200";
    case "in_review":
    case "in_progress":
    case "retest_needed":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "needs_update":
    case "blocked":
    case "blocker":
      return "bg-red-100 text-red-800 border-red-200";
    case "fixed":
      return "bg-blue-100 text-blue-800 border-blue-200";
    default:
      return "bg-muted text-muted-foreground border-gold/20";
  }
}

function ComplianceTab() {
  const fetcher = useServerFn(listCompliance);
  const updater = useServerFn(updateCompliance);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "compliance"],
    queryFn: () => fetcher(),
  });
  const mutation = useMutation({
    mutationFn: (vars: { id: string; status?: string; notes?: string | null; document_url?: string | null }) =>
      updater({ data: vars as any }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "compliance"] });
      toast.success("Saved");
    },
    onError: (e: any) => toast.error(e?.message || "Save failed"),
  });

  if (isLoading) return <div className="text-muted-foreground">Loading compliance…</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <ExportButtons
          rows={(data?.items ?? []) as any[]}
          columns={[
            { key: "title", header: "Document Name" },
            { key: "status", header: "Status" },
            { key: "last_updated_at", header: "Last Updated", get: (r: any) => r.last_updated_at ? new Date(r.last_updated_at).toISOString() : "" },
            { key: "updated_by", header: "Owner" },
            { key: "notes", header: "Notes" },
            { key: "document_url", header: "Document URL" },
          ]}
          filename="recovery-trauma-compliance"
          sheetName="Compliance"
        />
      </div>
      <div className="rounded-2xl border border-gold/20 bg-card overflow-hidden">
        <div className="overflow-x-auto">

        <table className="w-full text-sm">
          <thead className="bg-gold/5 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start p-3">Item</th>
              <th className="text-start p-3">Status</th>
              <th className="text-start p-3">Last updated</th>
              <th className="text-start p-3">Notes</th>
              <th className="text-start p-3">Document</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((item: any) => (
              <tr key={item.id} className="border-t border-gold/10 align-top">
                <td className="p-3 font-medium text-foreground">{item.title}</td>
                <td className="p-3">
                  <select
                    value={item.status}
                    onChange={(e) => mutation.mutate({ id: item.id, status: e.target.value })}
                    className={`rounded-md border px-2 py-1 text-xs ${statusBadgeClass(item.status)}`}
                  >
                    {COMPLIANCE_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </td>
                <td className="p-3 text-muted-foreground text-xs">
                  {item.last_updated_at ? new Date(item.last_updated_at).toLocaleString() : "—"}
                </td>
                <td className="p-3">
                  <textarea
                    defaultValue={item.notes ?? ""}
                    placeholder="Add notes…"
                    onBlur={(e) => {
                      if (e.target.value !== (item.notes ?? "")) {
                        mutation.mutate({ id: item.id, notes: e.target.value || null });
                      }
                    }}
                    className="w-full min-w-[200px] rounded-md border border-gold/20 bg-background p-2 text-xs"
                    rows={2}
                  />
                </td>
                <td className="p-3">
                  <div className="flex flex-col gap-1">
                    <input
                      type="url"
                      defaultValue={item.document_url ?? ""}
                      placeholder="https://…"
                      onBlur={(e) => {
                        if (e.target.value !== (item.document_url ?? "")) {
                          mutation.mutate({ id: item.id, document_url: e.target.value || null });
                        }
                      }}
                      className="w-full min-w-[180px] rounded-md border border-gold/20 bg-background p-1.5 text-xs"
                    />
                    {item.document_url && (
                      <a
                        href={item.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[var(--gold-deep)] underline"
                      >
                        View document
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}


// ============= Bugs Tab =============

const BUG_STATUSES = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "fixed", label: "Fixed" },
  { value: "retest_needed", label: "Retest Needed" },
  { value: "closed", label: "Closed" },
] as const;
const BUG_SEVERITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "blocker", label: "Launch Blocker" },
] as const;
const BUG_DEVICES = [
  { value: "mobile", label: "Mobile" },
  { value: "laptop", label: "Laptop" },
  { value: "desktop", label: "Desktop" },
] as const;

function BugsTab() {
  const fetcher = useServerFn(listBugs);
  const creator = useServerFn(createBug);
  const updater = useServerFn(updateBug);
  const remover = useServerFn(deleteBug);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "bugs"],
    queryFn: () => fetcher(),
  });
  const [showNew, setShowNew] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterSeverity, setFilterSeverity] = useState<string>("all");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "bugs"] });
  const createMut = useMutation({
    mutationFn: (vars: any) => creator({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Bug created"); setShowNew(false); },
    onError: (e: any) => toast.error(e?.message || "Create failed"),
  });
  const updateMut = useMutation({
    mutationFn: (vars: any) => updater({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Saved"); },
    onError: (e: any) => toast.error(e?.message || "Save failed"),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => remover({ data: { id } }),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
    onError: (e: any) => toast.error(e?.message || "Delete failed"),
  });

  const bugs = (data?.bugs ?? []).filter((b: any) => {
    if (filterStatus !== "all" && b.status !== filterStatus) return false;
    if (filterSeverity !== "all" && b.severity !== filterSeverity) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All statuses</option>
          {BUG_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All severities</option>
          {BUG_SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div className="ms-auto flex items-center gap-2">
          <ExportButtons
            rows={bugs as any[]}
            columns={[
              { key: "id", header: "Bug ID" },
              { key: "title", header: "Title" },
              { key: "description", header: "Description" },
              { key: "route", header: "Route" },
              { key: "device", header: "Device" },
              { key: "severity", header: "Severity" },
              { key: "status", header: "Status" },
              { key: "assigned_to", header: "Assigned To" },
              { key: "reported_at", header: "Date Reported", get: (r: any) => r.reported_at ? new Date(r.reported_at).toISOString() : "" },
              { key: "fixed_at", header: "Date Fixed", get: (r: any) => r.fixed_at ? new Date(r.fixed_at).toISOString() : "" },
              { key: "retest_notes", header: "Retest Notes" },
            ]}
            filename="recovery-trauma-bugs"
            sheetName="Bugs"
            filters={{ status: filterStatus, severity: filterSeverity }}
          />
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white hover:bg-gold/90"
          >
            <Plus className="h-4 w-4" /> New bug
          </button>
        </div>
      </div>


      {showNew && (
        <BugForm
          onCancel={() => setShowNew(false)}
          onSubmit={(values) => createMut.mutate(values)}
          submitting={createMut.isPending}
        />
      )}

      {isLoading ? (
        <div className="text-muted-foreground">Loading bugs…</div>
      ) : bugs.length === 0 ? (
        <div className="rounded-2xl border border-gold/20 bg-card p-8 text-center text-muted-foreground">
          No bugs match your filters.
        </div>
      ) : (
        <div className="space-y-3">
          {bugs.map((b: any) => (
            <BugRow
              key={b.id}
              bug={b}
              onUpdate={(patch) => updateMut.mutate({ id: b.id, ...patch })}
              onDelete={() => { if (confirm("Delete this bug?")) delMut.mutate(b.id); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BugForm({ onCancel, onSubmit, submitting }: {
  onCancel: () => void;
  onSubmit: (values: any) => void;
  submitting: boolean;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [route, setRoute] = useState("");
  const [device, setDevice] = useState("desktop");
  const [severity, setSeverity] = useState("medium");
  const [assignedTo, setAssignedTo] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState("");

  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-4 space-y-3">
      <h3 className="font-display text-lg">New bug</h3>
      <input className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Title*" value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      <div className="grid gap-2 sm:grid-cols-2">
        <input className="rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Page / route (e.g. /admin)" value={route} onChange={(e) => setRoute(e.target.value)} />
        <input className="rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Assigned to" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} />
        <select className="rounded-md border border-gold/20 bg-background p-2 text-sm" value={device} onChange={(e) => setDevice(e.target.value)}>
          {BUG_DEVICES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        <select className="rounded-md border border-gold/20 bg-background p-2 text-sm" value={severity} onChange={(e) => setSeverity(e.target.value)}>
          {BUG_SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <input className="rounded-md border border-gold/20 bg-background p-2 text-sm sm:col-span-2" placeholder="Screenshot URL (paste any image link)" value={screenshotUrl} onChange={(e) => setScreenshotUrl(e.target.value)} />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="rounded-lg border border-gold/20 px-4 py-2 text-sm">Cancel</button>
        <button
          disabled={submitting || !title.trim()}
          onClick={() => onSubmit({
            title: title.trim(),
            description: description || undefined,
            route: route || undefined,
            device,
            severity,
            assigned_to: assignedTo || undefined,
            screenshot_url: screenshotUrl || undefined,
          })}
          className="rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {submitting ? "Creating…" : "Create bug"}
        </button>
      </div>
    </div>
  );
}

function BugRow({ bug, onUpdate, onDelete }: { bug: any; onUpdate: (patch: any) => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-md border px-2 py-0.5 text-xs ${statusBadgeClass(bug.severity)}`}>{bug.severity}</span>
            <span className={`rounded-md border px-2 py-0.5 text-xs ${statusBadgeClass(bug.status)}`}>{bug.status.replace(/_/g, " ")}</span>
            <span className="text-xs text-muted-foreground">{bug.device}</span>
            {bug.route && <span className="text-xs text-muted-foreground">· {bug.route}</span>}
          </div>
          <h4 className="mt-1 font-medium text-foreground">{bug.title}</h4>
          {bug.description && <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{bug.description}</p>}
          <div className="mt-1 text-xs text-muted-foreground">
            Reported {new Date(bug.reported_at).toLocaleDateString()}
            {bug.assigned_to && <> · Assigned to {bug.assigned_to}</>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <select
            value={bug.status}
            onChange={(e) => onUpdate({ status: e.target.value })}
            className="rounded-md border border-gold/20 bg-background px-2 py-1 text-xs"
          >
            {BUG_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={() => setOpen((v) => !v)} className="rounded-md border border-gold/20 px-2 py-1 text-xs">
            {open ? "Hide" : "Details"}
          </button>
          <button onClick={onDelete} className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-3 border-t border-gold/10 pt-3">
          {bug.screenshot_url && (
            <a href={bug.screenshot_url} target="_blank" rel="noopener noreferrer" className="block">
              <img loading="lazy" decoding="async" src={bug.screenshot_url} alt="Screenshot" className="max-h-64 rounded-md border border-gold/20" />
            </a>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Retest notes</label>
            <textarea
              defaultValue={bug.retest_notes ?? ""}
              onBlur={(e) => {
                if (e.target.value !== (bug.retest_notes ?? "")) {
                  onUpdate({ retest_notes: e.target.value || null });
                }
              }}
              className="mt-1 w-full rounded-md border border-gold/20 bg-background p-2 text-sm"
              rows={3}
              placeholder="After developer marks Fixed, record retest results here."
            />
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <select
              value={bug.severity}
              onChange={(e) => onUpdate({ severity: e.target.value })}
              className="rounded-md border border-gold/20 bg-background p-2 text-sm"
            >
              {BUG_SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              value={bug.device}
              onChange={(e) => onUpdate({ device: e.target.value })}
              className="rounded-md border border-gold/20 bg-background p-2 text-sm"
            >
              {BUG_DEVICES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <input
              defaultValue={bug.assigned_to ?? ""}
              onBlur={(e) => {
                if (e.target.value !== (bug.assigned_to ?? "")) onUpdate({ assigned_to: e.target.value || null });
              }}
              placeholder="Assigned to"
              className="rounded-md border border-gold/20 bg-background p-2 text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============= Launch Tasks Tab =============

const TASK_CATEGORIES = [
  "Compliance", "Bugs", "UX", "Mobile", "Desktop", "Content",
  "Therapist Directory", "Community", "Payments", "Launch", "Marketing",
];
const TASK_STATUSES = [
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In Progress" },
  { value: "done", label: "Done" },
  { value: "blocked", label: "Blocked" },
] as const;
const TASK_PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
] as const;

function LaunchTasksTab() {
  const fetcher = useServerFn(listLaunchTasks);
  const creator = useServerFn(createLaunchTask);
  const updater = useServerFn(updateLaunchTask);
  const remover = useServerFn(deleteLaunchTask);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "launch-tasks"],
    queryFn: () => fetcher(),
  });
  const [showNew, setShowNew] = useState(false);
  const [filterCat, setFilterCat] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [showCompleted, setShowCompleted] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "launch-tasks"] });

  const createMut = useMutation({
    mutationFn: (vars: any) => creator({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Task added"); setShowNew(false); },
    onError: (e: any) => toast.error(e?.message || "Add failed"),
  });
  const updateMut = useMutation({
    mutationFn: (vars: any) => updater({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Saved"); },
    onError: (e: any) => toast.error(e?.message || "Save failed"),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => remover({ data: { id } }),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
    onError: (e: any) => toast.error(e?.message || "Delete failed"),
  });

  const tasks = (data?.tasks ?? []).filter((t: any) => {
    if (filterCat !== "all" && t.category !== filterCat) return false;
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    return true;
  });

  const outstanding = tasks.filter((t: any) => t.status !== "done");
  const completed = tasks.filter((t: any) => t.status === "done");

  const renderTable = (rows: any[]) => (
    <div className="rounded-2xl border border-gold/20 bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gold/5 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start p-3">Task</th>
              <th className="text-start p-3">Category</th>
              <th className="text-start p-3">Priority</th>
              <th className="text-start p-3">Status</th>
              <th className="text-start p-3">Owner</th>
              <th className="text-start p-3">Due</th>
              <th className="text-start p-3">Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t: any) => (
              <tr key={t.id} className="border-t border-gold/10 align-top">
                <td className="p-3 font-medium text-foreground">{t.title}</td>
                <td className="p-3 text-xs">{t.category}</td>
                <td className="p-3">
                  <select
                    value={t.priority}
                    onChange={(e) => updateMut.mutate({ id: t.id, priority: e.target.value })}
                    className={`rounded-md border px-2 py-1 text-xs ${statusBadgeClass(t.priority === "high" ? "blocker" : t.priority === "medium" ? "in_progress" : "default")}`}
                  >
                    {TASK_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <select
                    value={t.status}
                    onChange={(e) => updateMut.mutate({ id: t.id, status: e.target.value })}
                    className={`rounded-md border px-2 py-1 text-xs ${statusBadgeClass(t.status)}`}
                  >
                    {TASK_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <input
                    defaultValue={t.owner ?? ""}
                    onBlur={(e) => {
                      if (e.target.value !== (t.owner ?? "")) updateMut.mutate({ id: t.id, owner: e.target.value || null });
                    }}
                    className="w-28 rounded-md border border-gold/20 bg-background p-1 text-xs"
                  />
                </td>
                <td className="p-3">
                  <input
                    type="date"
                    defaultValue={t.due_date ?? ""}
                    onBlur={(e) => {
                      if (e.target.value !== (t.due_date ?? "")) updateMut.mutate({ id: t.id, due_date: e.target.value || null });
                    }}
                    className="rounded-md border border-gold/20 bg-background p-1 text-xs"
                  />
                </td>
                <td className="p-3">
                  <textarea
                    defaultValue={t.notes ?? ""}
                    onBlur={(e) => {
                      if (e.target.value !== (t.notes ?? "")) updateMut.mutate({ id: t.id, notes: e.target.value || null });
                    }}
                    rows={1}
                    className="w-full min-w-[150px] rounded-md border border-gold/20 bg-background p-1 text-xs"
                  />
                </td>
                <td className="p-3">
                  <button
                    onClick={() => { if (confirm("Delete this task?")) delMut.mutate(t.id); }}
                    className="rounded-md border border-red-200 bg-red-50 p-1 text-red-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );



  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All categories</option>
          {TASK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All statuses</option>
          {TASK_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div className="ms-auto flex items-center gap-2">
          <ExportButtons
            rows={tasks as any[]}
            columns={[
              { key: "title", header: "Task" },
              { key: "category", header: "Category" },
              { key: "priority", header: "Priority" },
              { key: "status", header: "Status" },
              { key: "owner", header: "Owner" },
              { key: "due_date", header: "Due Date" },
              { key: "notes", header: "Notes" },
            ]}
            filename="recovery-trauma-launch-tasks"
            sheetName="Launch Tasks"
            filters={{ category: filterCat, status: filterStatus }}
          />
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white hover:bg-gold/90"
          >
            <Plus className="h-4 w-4" /> New task
          </button>
        </div>
      </div>


      {showNew && (
        <TaskForm
          onCancel={() => setShowNew(false)}
          onSubmit={(values) => createMut.mutate(values)}
          submitting={createMut.isPending}
        />
      )}

      {isLoading ? (
        <div className="text-muted-foreground">Loading tasks…</div>
      ) : tasks.length === 0 ? (
        <div className="rounded-2xl border border-gold/20 bg-card p-8 text-center text-muted-foreground">
          No tasks match your filters.
        </div>
      ) : (
        <div className="space-y-6">
          <section className="space-y-2">
            <h3 className="font-display text-lg">
              To do <span className="text-sm text-muted-foreground">({outstanding.length})</span>
            </h3>
            {outstanding.length === 0 ? (
              <div className="rounded-2xl border border-gold/20 bg-card p-6 text-center text-muted-foreground">
                Nothing outstanding in this view.
              </div>
            ) : (
              renderTable(outstanding)
            )}
          </section>

          <section className="space-y-2">
            <div className="flex items-center gap-3">
              <h3 className="font-display text-lg">
                Completed <span className="text-sm text-muted-foreground">({completed.length})</span>
              </h3>
              <button
                onClick={() => setShowCompleted((v) => !v)}
                className="rounded-lg border border-gold/20 px-3 py-1 text-xs"
              >
                {showCompleted ? "Hide" : "Show"}
              </button>
            </div>
            {showCompleted &&
              (completed.length === 0 ? (
                <div className="rounded-2xl border border-gold/20 bg-card p-6 text-center text-muted-foreground">
                  No completed tasks in this view.
                </div>
              ) : (
                renderTable(completed)
              ))}
          </section>
        </div>
      )}
    </div>
  );
}

function TaskForm({ onCancel, onSubmit, submitting }: {
  onCancel: () => void;
  onSubmit: (values: any) => void;
  submitting: boolean;
}) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Launch");
  const [priority, setPriority] = useState("medium");
  const [owner, setOwner] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");

  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-4 space-y-3">
      <h3 className="font-display text-lg">New launch task</h3>
      <input className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Title*" value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="grid gap-2 sm:grid-cols-2">
        <select className="rounded-md border border-gold/20 bg-background p-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
          {TASK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="rounded-md border border-gold/20 bg-background p-2 text-sm" value={priority} onChange={(e) => setPriority(e.target.value)}>
          {TASK_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <input className="rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
        <input type="date" className="rounded-md border border-gold/20 bg-background p-2 text-sm" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </div>
      <textarea className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="rounded-lg border border-gold/20 px-4 py-2 text-sm">Cancel</button>
        <button
          disabled={submitting || !title.trim()}
          onClick={() => onSubmit({
            title: title.trim(),
            category,
            priority,
            owner: owner || undefined,
            due_date: dueDate || undefined,
            notes: notes || undefined,
          })}
          className="rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {submitting ? "Adding…" : "Add task"}
        </button>
      </div>
    </div>
  );
}

// ============= Members Tab =============

type MemberSegment =
  | "all"
  | "free"
  | "active_paid"
  | "failed_payments"
  | "cancelled_paid"
  | "never_logged_in"
  | "created_never_purchased"
  | "logged_in_today"
  | "logged_in_week"
  | "email_unverified"
  | "pending_activation"
  | "registered_unpaid"
  | "active_founding"
  | "cancelled_members"
  | "failed_payments_ms";

function MembersTab() {
  const statsFetcher = useServerFn(getMemberStats);
  const funnelFetcher = useServerFn(getConversionFunnel);
  const listFetcher = useServerFn(listMembers);
  const updater = useServerFn(updateMember);
  const exporter = useServerFn(exportMemberData);
  const deleter = useServerFn(deleteUser);
  const actionSender = useServerFn(sendMemberActionEmail);
  const manualVerifier = useServerFn(manuallyVerifyEmail);
  const qc = useQueryClient();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [funnelDays, setFunnelDays] = useState<7 | 30 | 90>(30);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null));
  }, []);

  const [search, setSearch] = useState("");
  const [tier, setTier] = useState<"all" | "free" | "healing">("all");
  const [status, setStatus] = useState<"all" | "active" | "cancelled" | "suspended">("all");
  const [segment, setSegment] = useState<MemberSegment>("all");
  const [sortBy, setSortBy] = useState<
    | "signed_up_desc"
    | "signed_up_asc"
    | "name_asc"
    | "name_desc"
    | "last_login_desc"
    | "subscription_start_desc"
  >("signed_up_desc");
  // Persisted in the URL so the selection survives searching, sorting,
  // opening a member row and coming back, and tab switches.
  const { mtype: membershipType = "all" } = Route.useSearch() as {
    mtype?: MembershipTypeFilter;
  };
  const membersNavigate = useNavigate();
  const setMembershipType = (next: MembershipTypeFilter) =>
    membersNavigate({
      to: "/admin",
      search: (prev: any) => ({ ...prev, tab: "members", mtype: next }),
      replace: true,
    });

  const { data: statsData } = useQuery({
    queryKey: ["admin", "member-stats"],
    queryFn: () => statsFetcher(),
    enabled: !!currentUserId,
  });
  const { data: funnelData } = useQuery({
    queryKey: ["admin", "conversion-funnel", funnelDays],
    queryFn: () => funnelFetcher({ data: { days: funnelDays } }),
    enabled: !!currentUserId,
    staleTime: 60_000,
  });
  const { data: listData, isLoading } = useQuery({
    queryKey: ["admin", "members", search, tier, status, segment, membershipType, sortBy],
    queryFn: () => listFetcher({ data: { search: search || undefined, tier, status, segment, membershipType, sortBy } }),
    enabled: !!currentUserId,
  });

  // Cancelled/expired members are hidden from the members list unless the
  // admin explicitly filters for them.
  const showCancelled = status === "cancelled" || segment === "cancelled_paid";
  const visibleMembers = ((listData?.members ?? []) as any[]).filter((m) =>
    showCancelled
      ? true
      : !(
          m.membership_status === "cancelled" ||
          m.membership_status === "expired"
        ),
  );




  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "members"] });
    qc.invalidateQueries({ queryKey: ["admin", "member-stats"] });
  };
  const updateMut = useMutation({
    mutationFn: (vars: any) => updater({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Saved"); },
    onError: (e: any) => toast.error(e?.message || "Save failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (userId: string) => deleter({ data: { userId } }),
    onSuccess: () => { invalidate(); toast.success("Account deleted"); },
    onError: (e: any) => toast.error(e?.message || "Delete failed"),
  });
  const actionMut = useMutation({
    mutationFn: (vars: { userId: string; action: "resend_verification" | "resend_activation" | "membership_invitation" | "payment_update" | "winback" | "comp_expiring" }) =>
      actionSender({ data: vars }),
    onSuccess: (res: any) => { invalidate(); toast.success(`Email sent to ${res?.email ?? "member"}`); },
    onError: (e: any) => toast.error(e?.message || "Send failed"),
  });

  const [verifyTarget, setVerifyTarget] = useState<{ userId: string; email: string } | null>(null);
  const manualVerifyMut = useMutation({
    mutationFn: (userId: string) => manualVerifier({ data: { userId } }),
    onSuccess: (res: any) => {
      invalidate();
      setVerifyTarget(null);
      toast.success(`Email verified for ${res?.email ?? "user"} — status is now ${res?.newStatus ?? "registered_unpaid"}.`);
    },
    onError: (e: any) => toast.error(e?.message || "Manual verification failed"),
  });


  type BulkTarget = { user_id: string; email: string };
  const [bulkVerify, setBulkVerify] = useState<{
    running: boolean;
    total: number;
    done: number;
    sent: number;
    failed: number;
    currentEmail: string | null;
    failedTargets: BulkTarget[];
    lastMode: "initial" | "retry" | null;
  }>({ running: false, total: 0, done: 0, sent: 0, failed: 0, currentEmail: null, failedTargets: [], lastMode: null });
  const bulkCancelRef = useRef(false);

  const runBulkVerification = async (targets: BulkTarget[], mode: "initial" | "retry") => {
    if (targets.length === 0) {
      toast.info(mode === "retry" ? "No failed sends to retry." : "No pending users with unverified emails in the current view.");
      return;
    }
    const promptMsg = mode === "retry"
      ? `Retry verification email for ${targets.length} previously failed user${targets.length === 1 ? "" : "s"}?`
      : `Resend verification email to ${targets.length} user${targets.length === 1 ? "" : "s"}?`;
    if (!window.confirm(promptMsg)) return;

    bulkCancelRef.current = false;
    setBulkVerify({
      running: true,
      total: targets.length,
      done: 0,
      sent: 0,
      failed: 0,
      currentEmail: null,
      failedTargets: [],
      lastMode: mode,
    });
    let sent = 0;
    let failed = 0;
    const newFailed: BulkTarget[] = [];
    for (let i = 0; i < targets.length; i++) {
      if (bulkCancelRef.current) break;
      const t = targets[i];
      setBulkVerify((s) => ({ ...s, currentEmail: t.email }));
      try {
        await actionSender({ data: { userId: t.user_id, action: "resend_verification" } });
        sent += 1;
      } catch {
        failed += 1;
        newFailed.push(t);
      }
      setBulkVerify((s) => ({ ...s, done: i + 1, sent, failed, failedTargets: newFailed }));
      await new Promise((r) => setTimeout(r, 250));
    }
    setBulkVerify((s) => ({ ...s, running: false, currentEmail: null, failedTargets: newFailed }));
    invalidate();
    if (failed === 0) toast.success(`Verification ${mode === "retry" ? "retried for" : "resent to"} ${sent} user${sent === 1 ? "" : "s"}.`);
    else toast.warning(`Sent ${sent}, failed ${failed}. Use "Retry failed" to try again.`);
  };

  const runBulkResendVerification = () => {
    const all = (listData?.members ?? []) as any[];
    const pending: BulkTarget[] = all
      .filter((m) => (m.email_verified === false || m.membership_status === "email_unverified") && !!m.email)
      .map((m) => ({ user_id: m.user_id, email: m.email }));
    return runBulkVerification(pending, "initial");
  };

  const runRetryFailedVerification = () => runBulkVerification(bulkVerify.failedTargets, "retry");


  const exportUser = async (userId: string, name: string) => {
    try {
      const data = await exporter({ data: { userId } });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `member-${(name || userId).replace(/[^a-z0-9_-]+/gi, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    }
  };

  const s = statsData?.stats;
  const j = (statsData as any)?.journey as
    | { emailUnverified: number; pendingActivation: number; registeredUnpaid: number; activeFounding: number; cancelledMemberships: number; failedPayments: number }
    | undefined;

  const quickFilters: Array<{ id: MemberSegment; label: string }> = [
    { id: "all", label: "All" },
    { id: "never_logged_in", label: "Never logged in" },
    { id: "logged_in_today", label: "Logged in today" },
    { id: "logged_in_week", label: "Logged in this week" },
    { id: "email_unverified", label: "Email unverified" },
    { id: "pending_activation", label: "Pending activation" },
    { id: "registered_unpaid", label: "Payment required" },
    { id: "active_founding", label: "Paid Founding Members" },
    { id: "cancelled_members", label: "Cancelled members" },
    { id: "failed_payments_ms", label: "Failed payments" },
  ];

  return (
    <div className="space-y-6">
      <MembershipTruthPanel />

      {j && (
        <div>
          <h3 className="mb-2 font-display text-sm uppercase tracking-wider text-muted-foreground">
            Sign-ups without a payment
          </h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <StatCard label="Registered, not paid" value={j.registeredUnpaid} />
          </div>
        </div>
      )}

      {s && (
        <div>
          <h3 className="mb-2 font-display text-sm uppercase tracking-wider text-muted-foreground">
            Other
          </h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {/* Therapists and Admins are not members — kept out of member stats. */}
            <StatCard label="New This Month" value={s.newThisMonth} />
          </div>
        </div>
      )}

      {funnelData && (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-display text-sm uppercase tracking-wider text-muted-foreground">
                Payment journey
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">Unique visitors, with recent activity given 30 minutes before it counts as abandoned.</p>
            </div>
            <div className="flex gap-1" aria-label="Payment journey date range">
              {([7, 30, 90] as const).map((days) => (
                <Button
                  key={days}
                  type="button"
                  variant="outline"
                  onClick={() => setFunnelDays(days)}
                  className={`min-h-11 rounded-full border px-3 text-xs font-medium transition ${
                    funnelDays === days
                      ? "border-gold bg-gold/15 text-foreground"
                      : "border-border bg-background text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {days} days
                </Button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <div className="rounded-2xl border border-gold/20 bg-card/60 p-4">
              <div className="space-y-3">
                {funnelData.stages.map((stage: any, i: number) => (
                  <div key={stage.key}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium text-foreground">
                        {i + 1}. {stage.label}
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        <strong className="text-foreground">{stage.count}</strong>
                        {i > 0 && (
                          <span className="ms-2 text-xs">
                            <span className="text-emerald-600">{stage.stepPct}% step</span>
                            {stage.dropoff > 0 && (
                              <span className="ms-2 text-rose-600">
                                −{stage.dropoff} ({stage.dropoffPct}%)
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-gold to-[var(--gold-deep)] transition-all"
                        style={{ width: `${Math.max(stage.pct, 2)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Distinct payment journeys since{" "}
                {new Date((funnelData as any).since).toLocaleDateString("en-GB")}.
              </p>
            </div>
            <div className="rounded-2xl border border-gold/20 bg-card/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Where visitors stopped</p>
              <div className="mt-3 space-y-2 text-sm">
                {([
                  ["Reached payment, chose nothing", (funnelData as any).dropoff.reachedNotSelected],
                  ["Chose provider, checkout did not open", (funnelData as any).dropoff.selectedNotOpened],
                  ["Checkout opened, payment not completed", (funnelData as any).dropoff.openedNotCompleted],
                  ["Returned from provider unpaid", (funnelData as any).dropoff.returnedFromProviderUnpaid],
                  ["System checkout failures", (funnelData as any).dropoff.checkoutFailures],
                ] as Array<[string, number]>).map(([label, count]) => (
                  <div key={label} className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-background/60 px-3 py-2">
                    <span>{label}</span><strong className="tabular-nums">{count}</strong>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-xl border border-gold/15 bg-background/60 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Provider checkout opened</p>
                <ul className="mt-2 space-y-1 text-sm tabular-nums">
                  {(["stripe", "paypal", "apple"] as const).map((provider) => (
                    <li key={provider} className="flex justify-between"><span className="capitalize">{provider}</span><strong>{(funnelData as any).providers.checkoutOpened[provider] ?? 0}</strong></li>
                  ))}
                </ul>
              </div>
              <div className="mt-4 rounded-xl border border-gold/15 bg-background/60 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Verified payments</p>
                <ul className="mt-2 space-y-1 text-sm tabular-nums">
                  {(["stripe", "paypal", "apple"] as const).map((provider) => (
                    <li key={provider} className="flex justify-between"><span className="capitalize">{provider}</span><strong>{(funnelData as any).providers.paymentCompleted[provider] ?? 0}</strong></li>
                  ))}
                </ul>
              </div>
              {(funnelData as any).failureReasons.length > 0 && <div className="mt-4"><p className="text-xs font-semibold uppercase tracking-wide text-destructive">System failure reasons</p><ul className="mt-2 space-y-1 text-xs text-muted-foreground">{(funnelData as any).failureReasons.map((item: any) => <li key={item.reason} className="flex justify-between gap-3"><span>{item.reason}</span><strong>{item.count}</strong></li>)}</ul></div>}
            </div>
          </div>
        </div>
      )}



      <div className="flex flex-wrap gap-2">
        {quickFilters.map((f) => (
          <button
            key={f.id}
            onClick={() => setSegment(f.id)}
            className={`rounded-full border px-3 py-1 text-xs transition ${
              segment === f.id
                ? "border-gold bg-gold text-white"
                : "border-gold/20 bg-card text-muted-foreground hover:bg-gold/10"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gold/20 bg-card/60 p-3">
        <div className="text-sm">
          <div className="font-semibold text-foreground">Bulk: resend verification</div>
          <div className="text-xs text-muted-foreground">
            Sends the verification email to every unverified user in the current filtered view.
          </div>
        </div>
        <div className="ms-auto flex items-center gap-3">
          {bulkVerify.running && (
            <div className="flex items-center gap-2">
              <div className="h-2 w-40 overflow-hidden rounded-full bg-gold/10">
                <div
                  className="h-full bg-gold transition-all"
                  style={{ width: `${bulkVerify.total ? Math.round((bulkVerify.done / bulkVerify.total) * 100) : 0}%` }}
                />
              </div>
              <div className="text-xs text-muted-foreground tabular-nums">
                {bulkVerify.done}/{bulkVerify.total} · sent {bulkVerify.sent}
                {bulkVerify.failed > 0 ? ` · failed ${bulkVerify.failed}` : ""}
              </div>
              <button
                onClick={() => { bulkCancelRef.current = true; }}
                className="rounded-md border border-gold/30 px-2 py-1 text-xs text-muted-foreground hover:bg-gold/10"
              >
                Cancel
              </button>
            </div>
          )}
          {!bulkVerify.running && bulkVerify.total > 0 && (
            <div className="text-xs text-muted-foreground">
              Last run: sent {bulkVerify.sent}
              {bulkVerify.failed > 0 ? `, failed ${bulkVerify.failed}` : ""} of {bulkVerify.total}
            </div>
          )}
          {!bulkVerify.running && bulkVerify.failedTargets.length > 0 && (
            <button
              onClick={runRetryFailedVerification}
              className="rounded-full border border-red-300 bg-red-50 px-4 py-2 text-xs font-semibold text-red-800 hover:bg-red-100"
              title={bulkVerify.failedTargets.map((t) => t.email).join(", ")}
            >
              Retry {bulkVerify.failedTargets.length} failed
            </button>
          )}
          <button
            onClick={runBulkResendVerification}
            disabled={bulkVerify.running}
            className="rounded-full bg-gold px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-gold/90 disabled:opacity-50"
          >
            {bulkVerify.running
              ? bulkVerify.lastMode === "retry" ? "Retrying…" : "Sending…"
              : "Resend to all pending"}
          </button>
        </div>
      </div>

      <ProviderAuditPanel />

      

      <div>

        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Membership type
        </p>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by membership type">
          {MEMBERSHIP_TYPE_FILTERS.map((key) => {
            const active = membershipType === key;
            const count = (listData as any)?.categoryCounts?.[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setMembershipType(key)}
                aria-pressed={active}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "border-gold bg-gold/15 text-foreground shadow-sm"
                    : "border-gold/20 bg-card text-muted-foreground hover:bg-gold/5"
                }`}
              >
                {MEMBERSHIP_TYPE_LABEL[key]}
                {typeof count === "number" ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>
      </div>


      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute start-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name…"
            className="rounded-md border border-gold/20 bg-card ps-8 pe-3 py-2 text-sm w-56"
          />
        </div>
        <select value={tier} onChange={(e) => setTier(e.target.value as any)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All tiers</option>
          <option value="free">Free Account (Payment Required)</option>
          <option value="healing">Founding Member</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="cancelled">Cancelled</option>
          <option value="suspended">Suspended</option>
        </select>
        <select value={segment} onChange={(e) => setSegment(e.target.value as any)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All members</option>
          <option value="free">Free Accounts (Payment Required)</option>
          <option value="active_paid">Active paid members</option>
          <option value="failed_payments">Failed payments</option>
          <option value="cancelled_paid">Cancelled members</option>
          <option value="never_logged_in">Never logged in</option>
          <option value="created_never_purchased">Created account, never purchased</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="signed_up_desc">Sign-up date: newest first</option>
          <option value="signed_up_asc">Sign-up date: oldest first</option>
          <option value="name_asc">Name: A–Z</option>
          <option value="name_desc">Name: Z–A</option>
          <option value="last_login_desc">Last login: newest first</option>
          <option value="subscription_start_desc">Subscription start: newest first</option>
        </select>
        <div className="ms-auto">
          <ExportButtons
            rows={visibleMembers as any[]}
            columns={[
              { key: "display_name", header: "Name" },
              { key: "email", header: "Email" },
              { key: "account_type", header: "Account Type" },
              { key: "membership_tier", header: "Membership Tier", get: (r: any) => r.access_kind === "paid_one_off" && String(r.paid_access_type ?? "").startsWith("october") ? "Founding Member 1 Month" : r.membership_tier === "healing" ? "Paid Founding Member" : "Free Account (Payment Required)" },
              { key: "membership_type", header: "Membership Type", get: (r: any) => {
                const cat = (r.membership_category ?? (r.membership_type ?? "free")) as MembershipTypeFilter;
                const label = MEMBERSHIP_TYPE_LABEL[cat] ?? "";
                return cat === "complimentary" && r.complimentary_reason
                  ? `${label} (${r.complimentary_reason})`
                  : label;
              } },
              { key: "plan_label", header: "Plan", get: (r: any) => r.plan_label ?? "" },
              { key: "billing_duration_label", header: "Membership Duration", get: (r: any) => r.renewal_amount_cents != null ? (r.billing_duration_label ?? "Monthly") : "" },
              { key: "billing_cycle_amount_cents", header: "Price Paid", get: (r: any) => r.billing_cycle_amount_cents != null ? `$${(r.billing_cycle_amount_cents / 100).toFixed(2)}` : "" },
              { key: "next_renewal_amount_cents", header: "Next Renewal Amount", get: (r: any) => r.next_renewal_amount_cents != null ? `$${(r.next_renewal_amount_cents / 100).toFixed(2)}` : "" },

              { key: "membership_status", header: "Membership Status" },
              { key: "payment_provider", header: "Paid Via", get: (r: any) => r.payment_provider === "stripe" ? "Stripe" : r.payment_provider === "paypal" ? "PayPal" : r.payment_provider === "stripe_historical_paypal" ? "Stripe (historical PayPal)" : r.payment_provider === "paypal_historical_stripe" ? "PayPal (historical Stripe)" : r.payment_provider === "both" ? "Stripe + PayPal (both live)" : "" },
              { key: "paypal_subscription_id", header: "PayPal Subscription ID" },
              { key: "stripe_subscription_id", header: "Stripe Subscription ID" },
              { key: "provider_status_label", header: "Provider Status" },
              { key: "paid_access_active", header: "Paid Access Active", get: (r: any) => r.paid_access_active ? "Yes" : "No" },
              { key: "access_reason", header: "Access Reason" },
              { key: "payment_review_required", header: "Payment Review Required", get: (r: any) => r.payment_review_required ? "Yes" : "No" },
              { key: "subscription_started_at", header: "Subscription Start", get: (r: any) => r.subscription_started_at ? new Date(r.subscription_started_at).toISOString() : "" },
              { key: "next_renewal_at", header: "Next Renewal", get: (r: any) => r.next_renewal_at ? new Date(r.next_renewal_at).toISOString() : "" },
              { key: "cancellation_date", header: "Cancelled At", get: (r: any) => r.cancellation_date ? new Date(r.cancellation_date).toISOString() : "" },
              { key: "upgrade_link_sent_at", header: "Upgrade Link Sent", get: (r: any) => r.upgrade_link_sent_at ? new Date(r.upgrade_link_sent_at).toISOString() : "" },
              { key: "status", header: "Account Status" },
              { key: "country", header: "Country" },
              { key: "joined_at", header: "Join Date", get: (r: any) => r.joined_at ? new Date(r.joined_at).toISOString() : "" },
              { key: "last_login_at", header: "Last Login", get: (r: any) => r.last_login_at ? new Date(r.last_login_at).toISOString() : "" },
              { key: "admin_notes", header: "Notes" },
            ]}
            filename="recovery-trauma-members"
            sheetName="Members"
            filters={{ search, tier, status, segment, membershipType }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading members…</div>
      ) : (
        <div className="rounded-2xl border border-gold/20 bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gold/5 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-start p-3">Name</th>
                  <th className="text-start p-3">Email</th>
                  <th className="text-start p-3">Email verified</th>
                  <th className="text-start p-3">Account type</th>
                  <th className="text-start p-3">Tier</th>
                  <th className="text-start p-3">Membership type</th>
                  <th className="text-start p-3">Membership status</th>
                  <th className="text-start p-3">Paid via</th>
                  <th className="text-start p-3">Paid access</th>

                  <th className="text-start p-3">Account created</th>
                  <th className="text-start p-3">Days since reg.</th>
                  <th className="text-start p-3">First login</th>
                  <th className="text-start p-3">Last login</th>
                  <th className="text-start p-3">Sub start</th>
                  <th className="text-start p-3">Next renewal</th>
                  <th className="text-start p-3">Cancelled</th>
                  <th className="text-start p-3">Upgrade link sent</th>
                  <th className="text-start p-3">Marketing</th>
                  <th className="text-start p-3">Newsletter</th>
                  <th className="text-start p-3">Reminders</th>
                  <th className="text-start p-3">Last marketing</th>
                  <th className="text-start p-3">Last onboarding</th>
                  <th className="text-start p-3">Account status</th>
                  <th className="sticky end-0 z-20 border-s border-gold/20 bg-card text-end p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleMembers.map((m: any) => {
                  const st = m.membership_status as string | null;
                  const compEndsAt =
                    m.membership_category === "complimentary"
                      ? parseTimestamp(m.complimentary_expires_at)
                      : null;
                  const recommendedAction:
                    | { label: string; action: "resend_verification" | "resend_activation" | "membership_invitation" | "payment_update" | "winback" | "comp_expiring" }
                    | null =
                    st === "email_unverified" || m.email_verified === false
                      ? { label: "Resend verification", action: "resend_verification" }
                      : st === "pending_activation"
                        ? { label: "Resend activation", action: "resend_activation" }
                        // Anyone with an active one-off pass (October offer, gift
                        // redemption) already has paid access — never invite them to pay.
                        : st === "registered_unpaid" && m.access_kind !== "paid_one_off"
                          ? { label: "Send invitation", action: "membership_invitation" }
                          : st === "payment_failed" && m.access_kind !== "paid_one_off"
                            ? { label: "Send payment update", action: "payment_update" }
                            : compEndsAt
                              ? { label: "Invite to subscribe (comp ending)", action: "comp_expiring" }
                              : (st === "cancelled" || st === "expired" || !!m.cancellation_date)
                                ? { label: "Send win-back offer", action: "winback" }
                                : null;
                  return (
                  <tr key={m.user_id} className="border-t border-gold/10 align-top">
                    <td className="p-3 font-medium text-foreground">
                      <div>{m.display_name || "—"}</div>
                      <div className="mt-0.5 text-xs font-normal text-muted-foreground">
                        Signed up{" "}
                        {m.account_created_at
                          ? new Date(m.account_created_at).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </div>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{m.email || "—"}</td>
                    <td className="p-3 text-xs">
                      <span className={m.email_verified ? "text-emerald-700 font-semibold" : "text-amber-700"}>
                        {m.email_verified ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="p-3">
                      {m.signup_payment_label ? (
                        <span
                          className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-900"
                          title="This person selected a tier but no payment went through — they have no member access."
                        >
                          {m.signup_payment_label}
                        </span>
                      ) : (
                        <span className={`rounded-full px-2 py-0.5 text-xs ${m.account_type === "paid" ? "bg-emerald-100 text-emerald-900" : "bg-muted text-foreground"}`}>
                          {m.account_type === "paid" ? "Paid Founding Member" : "Free Account (Payment Required)"}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {m.access_kind === "paid_one_off" && String(m.paid_access_type ?? "").startsWith("october") ? (
                        <span
                          className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-900"
                          title="October $4.99 pass — 30 days, one-time payment, no renewal"
                        >
                          Founding Member 1 Month
                        </span>
                      ) : (
                      <select
                        value={m.membership_tier || "free"}
                        onChange={(e) => updateMut.mutate({ userId: m.user_id, membership_tier: e.target.value })}
                        className="rounded-md border border-gold/20 bg-background px-2 py-1 text-xs"
                        title="Guarded by DB trigger: active paid members can't be silently downgraded."
                      >
                        <option value="free">Free Account (Payment Required)</option>
                        <option value="healing">Founding Member</option>
                      </select>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {m.complimentary_source === "instagram_giveaway" && (
                        <div className="mb-1">
                          <span
                            className="inline-block rounded-full bg-[var(--gold-light,#f6ecd2)] px-2 py-0.5 font-semibold text-[var(--gold-deep)] ring-1 ring-gold/40"
                            title="Instagram giveaway winner — complimentary full membership, £0, no auto-renewal"
                          >
                            🏆 {m.complimentary_label ?? "Instagram Giveaway Winner"}
                          </span>
                        </div>
                      )}
                      {m.membership_category === "complimentary" ? (
                        <span
                          className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-900"
                          title={[
                            m.complimentary_reason ? `Reason: ${m.complimentary_reason}` : null,
                            m.complimentary_granted_by ? `Granted by: ${m.complimentary_granted_by}` : null,
                            m.complimentary_granted_at ? `Granted: ${parseTimestamp(m.complimentary_granted_at)?.toLocaleDateString() ?? "—"}` : null,
                            compEndsAt ? `Ends: ${compEndsAt.toLocaleDateString()}` : null,
                          ].filter(Boolean).join(" · ")}
                        >
                          Complimentary{compEndsAt ? ` · ends ${compEndsAt.toLocaleDateString()}` : ""}
                        </span>
                      ) : m.membership_category === "admin" ? (
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 font-medium text-slate-900">
                          Admin
                        </span>
                      ) : m.membership_category === "therapist" ? (
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 font-medium text-sky-900">
                          Therapist
                        </span>
                      ) : m.membership_category === "paid" ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-900">
                          Paid subscription
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Free — payment required</span>
                      )}
                      {m.membership_category === "complimentary" && m.complimentary_reason && (
                        <div className="mt-1 text-[11px] leading-tight text-muted-foreground">
                          {m.complimentary_reason}
                        </div>
                      )}
                      {m.membership_category === "complimentary" && (
                        <div className="mt-1 space-y-0.5 text-[11px] leading-tight text-muted-foreground">
                          <div>
                            Access type:{" "}
                            {m.complimentary_source === "instagram_giveaway"
                              ? "Giveaway Winner"
                              : "Complimentary"}
                          </div>
                          <div>Price: £0 / Complimentary · Full Membership</div>
                          {m.complimentary_source === "instagram_giveaway" && (
                            <div>Source: Instagram Giveaway</div>
                          )}
                          <div>
                            Starts:{" "}
                            {m.complimentary_starts_at
                              ? parseTimestamp(m.complimentary_starts_at)?.toLocaleDateString() ?? "—"
                              : "—"}{" "}
                            · Expires:{" "}
                            {compEndsAt ? compEndsAt.toLocaleDateString() : "—"}
                          </div>
                          <div>No auto-renewal — never charged automatically</div>
                        </div>
                      )}
                      {m.renewal_amount_cents != null && (
                        <div className="mt-1 rounded-md border border-gold/20 bg-gold/5 px-2 py-1.5 text-[11px] leading-tight">
                          {/* What they actually bought — duration + full price
                              paid, never a monthly equivalent. */}
                          <div className="flex flex-wrap items-center gap-1 font-semibold text-foreground">
                            <span className="rounded-sm bg-[var(--gold-deep)]/12 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[var(--gold-deep)]">
                              {(m.billing_duration_label ?? "Monthly").toUpperCase()}
                            </span>
                            <span>
                              {(m.billing_duration_label ?? "Monthly")} · $
                              {(
                                (m.billing_cycle_amount_cents ?? m.renewal_amount_cents) / 100
                              ).toFixed(2)}
                            </span>
                            <span className="font-normal text-muted-foreground">
                              ·{" "}
                              {m.payment_provider === "paypal" ||
                              m.payment_provider === "paypal_historical_stripe"
                                ? "PayPal"
                                : m.payment_provider === "stripe" ||
                                    m.payment_provider === "stripe_historical_paypal"
                                  ? "Stripe"
                                  : m.payment_provider === "both"
                                    ? "Stripe + PayPal"
                                    : "—"}
                            </span>
                          </div>

                          {m.upgraded_at && (
                            <div className="mt-0.5 font-medium text-[var(--gold-deep)]">
                              Upgraded{" "}
                              {new Date(m.upgraded_at).toLocaleDateString()}
                              {m.upgraded_from_plan_id
                                ? ` from ${
                                    m.upgraded_from_plan_id === "membership_monthly"
                                      ? "Monthly"
                                      : m.upgraded_from_plan_id === "membership_6month"
                                        ? "6 Months"
                                        : "Yearly"
                                  }`
                                : ""}
                              {m.upgrade_provider
                                ? ` · via ${m.upgrade_provider === "paypal" ? "PayPal" : "Stripe"}`
                                : ""}
                            </div>
                          )}



                          <div className="mt-0.5 text-muted-foreground">
                            Joined{" "}
                            {m.subscription_started_at
                              ? new Date(m.subscription_started_at).toLocaleDateString()
                              : m.joined_at
                                ? new Date(m.joined_at).toLocaleDateString()
                                : "—"}
                            {m.amount_paid_cents != null &&
                            m.amount_paid_cents !==
                              (m.billing_cycle_amount_cents ?? m.renewal_amount_cents)
                              ? ` · first payment $${(m.amount_paid_cents / 100).toFixed(2)}`
                              : ""}
                          </div>

                          {m.membership_status === "cancelled" ||
                          m.membership_status === "expired" ? (
                            <div className="mt-0.5 text-muted-foreground">
                              Cancelled
                              {m.cancellation_date
                                ? ` · ${new Date(m.cancellation_date).toLocaleDateString()}`
                                : ""}{" "}
                              · no further renewal
                            </div>
                          ) : m.cancel_at_period_end ? (
                            <div className="mt-0.5 text-amber-700">
                              Cancelling · access until
                              {m.access_ends_at
                                ? ` ${new Date(m.access_ends_at).toLocaleDateString()}`
                                : " their paid period ends"}{" "}
                              · no further renewal
                            </div>
                          ) : (
                            <div className="mt-0.5 text-emerald-700">
                              Active · next renewal
                              {m.next_renewal_at
                                ? ` ${new Date(m.next_renewal_at).toLocaleDateString()}`
                                : ""}
                              {m.next_renewal_amount_cents != null
                                ? ` · $${(m.next_renewal_amount_cents / 100).toFixed(2)}`
                                : ""}
                            </div>
                          )}
                        </div>
                      )}


                    </td>


                    <td className="p-3 text-xs">

                      {(() => {
                        const isOctoberPass =
                          m.access_kind === "paid_one_off" &&
                          String(m.paid_access_type ?? "").startsWith("october");
                        if (isOctoberPass) {
                          const ends = m.paid_access_expires_at
                            ? new Date(m.paid_access_expires_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                            : null;
                          return (
                            <span
                              className="rounded-full px-2 py-0.5 bg-emerald-100 font-medium text-emerald-900"
                              title="October $4.99 offer — 30 days, one-time payment, no renewal"
                            >
                              October
                            </span>
                          );
                        }
                        return m.membership_status ? (
                          <span className={`rounded-full px-2 py-0.5 ${
                            m.membership_status === "active" ? "bg-emerald-100 text-emerald-900"
                            : m.membership_status === "payment_failed" ? "bg-red-100 text-red-900"
                            : m.membership_status === "cancelled" || m.membership_status === "expired" ? "bg-muted text-foreground"
                            : "bg-amber-100 text-amber-900"
                          }`}>
                            {m.membership_status}
                          </span>
                        ) : "—";
                      })()}
                    </td>
                    <td className="p-3 text-xs">
                      {m.payment_provider ? (
                        <span
                          className={`rounded-full px-2 py-0.5 font-medium ${
                            m.payment_provider === "stripe" || m.payment_provider === "stripe_historical_paypal"
                              ? "bg-indigo-100 text-indigo-900"
                            : m.payment_provider === "paypal" || m.payment_provider === "paypal_historical_stripe"
                              ? "bg-sky-100 text-sky-900"
                            : "bg-purple-100 text-purple-900"
                          }`}
                          title={[
                            `Stripe: ${m.stripe_live ? `live (${m.stripe_live_status})` : "not live"} ${m.stripe_subscription_id ?? m.stripe_customer_id ?? ""}`.trim(),
                            `PayPal: ${m.paypal_live ? `live (${m.paypal_live_status})` : "not live"} ${m.paypal_subscription_id ?? ""} ${m.paypal_payer_email ?? ""}`.trim(),
                            m.provider_check_failed ? "⚠ live provider check failed — showing stored data" : "",
                          ].filter(Boolean).join("\n")}
                        >
                          {m.payment_provider === "stripe" ? "Stripe"
                            : m.payment_provider === "paypal" ? "PayPal"
                            : m.payment_provider === "stripe_historical_paypal" ? "Stripe (historical PayPal)"
                            : m.payment_provider === "paypal_historical_stripe" ? "PayPal (historical Stripe)"
                            : "Stripe + PayPal"}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      <div className="mt-1 space-y-0.5 text-[11px] leading-tight">
                        <div
                          className={
                            m.provider_status_tone === "ok" ? "text-emerald-700"
                            : m.provider_status_tone === "warn" ? "text-amber-700"
                            : m.provider_status_tone === "bad" ? "text-red-700"
                            : "text-muted-foreground"
                          }
                          title={m.access_reason ?? ""}
                        >
                          {m.provider_status_label ?? "—"}
                        </div>
                        {m.provider_check_failed && (
                          <div className="text-amber-700">⚠ live check unavailable</div>
                        )}
                        <details className="mt-1">
                          <summary className="cursor-pointer text-muted-foreground underline">
                            Diagnostics
                          </summary>
                          <dl className="mt-1 space-y-0.5 text-[10px] text-muted-foreground">
                            {([
                              ["Provider", m.payment_provider ?? "—"],
                              ["Stripe customer", m.stripe_customer_id ?? "—"],
                              ["Stripe subscription", m.stripe_subscription_id ?? "—"],
                              ["Stripe API status", m.stripe_api_status ?? (m.stripe_subscription_id ? (m.stripe_subscription_found ? "unknown" : "not found") : "—")],
                              ["PayPal subscription", m.paypal_subscription_id ?? "—"],
                              ["PayPal API status", m.paypal_api_status ?? (m.paypal_subscription_id ? (m.paypal_subscription_found ? "unknown" : "not found") : "—")],
                              ["PayPal stored status", m.paypal_stored_status ?? "—"],
                              ["PayPal subs on file", String(m.paypal_subscription_count ?? 0)],
                              ["Local membership status", m.membership_status ?? "—"],
                              ["Amount paid", m.amount_paid_cents != null ? `${(m.amount_paid_cents / 100).toFixed(2)} ${(m.currency ?? "USD").toUpperCase()}` : "—"],
                              ["Last payment", m.subscription_started_at ? new Date(m.subscription_started_at).toLocaleString() : "—"],
                              ["Next renewal", m.next_renewal_at ? new Date(m.next_renewal_at).toLocaleString() : "—"],
                              ["Cancelled", m.cancellation_date ? new Date(m.cancellation_date).toLocaleString() : "—"],
                              ["Grace until", m.grace_period_until ? new Date(m.grace_period_until).toLocaleString() : "—"],
                              ["Last webhook", m.last_webhook_at ? new Date(m.last_webhook_at).toLocaleString() : "—"],
                              ["Webhook event", m.last_webhook_event_id ?? "—"],
                              ["Last reconciled", m.last_reconciled_at ? new Date(m.last_reconciled_at).toLocaleString() : "—"],
                              ["Checkout session", m.checkout_session_id ?? "—"],
                              ["Access reason", m.access_reason ?? "—"],
                            ] as Array<[string, string]>).map(([k, v]) => (
                              <div key={k} className="flex gap-1">
                                <dt className="shrink-0 font-medium">{k}:</dt>
                                <dd className="break-all">{v}</dd>
                              </div>
                            ))}
                          </dl>
                        </details>
                      </div>

                    </td>

                    <td className="p-3 text-xs">
                      <span className={m.paid_access_active ? "font-semibold text-emerald-700" : "text-muted-foreground"}>
                        {m.paid_access_active ? "Yes" : "No"}
                      </span>
                      {m.payment_review_required && (
                        <div className="mt-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-900">
                          Payment review required
                        </div>
                      )}
                      {m.access_reason && (
                        <div className="mt-1 text-[10px] leading-tight text-muted-foreground">
                          {m.access_reason}
                        </div>
                      )}
                    </td>


                    <td className="p-3 text-xs text-muted-foreground">{m.account_created_at ? new Date(m.account_created_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{m.days_since_registration ?? "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{m.first_login_at ? new Date(m.first_login_at).toLocaleDateString() : "Never"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{m.last_login_at ? new Date(m.last_login_at).toLocaleDateString() : "Never"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{m.subscription_started_at ? new Date(m.subscription_started_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{m.next_renewal_at ? new Date(m.next_renewal_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{m.cancellation_date ? new Date(m.cancellation_date).toLocaleDateString() : "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground" title={m.upgrade_link_sent_at ?? ""}>{m.upgrade_link_sent_at ? new Date(m.upgrade_link_sent_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3 text-xs">
                      <span className={m.marketing_emails_opt_in ? "text-emerald-700" : "text-muted-foreground"}>
                        {m.marketing_emails_opt_in ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="p-3 text-xs">
                      <span className={m.newsletter_opt_in ? "text-emerald-700" : "text-muted-foreground"}>
                        {m.newsletter_opt_in ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="p-3 text-xs">
                      <span className={m.membership_reminders_opt_in ? "text-emerald-700" : "text-muted-foreground"}>
                        {m.membership_reminders_opt_in ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground" title={m.last_marketing_email_at ?? ""}>{m.last_marketing_email_at ? new Date(m.last_marketing_email_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground" title={m.last_onboarding_email_at ?? ""}>{m.last_onboarding_email_at ? new Date(m.last_onboarding_email_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3">
                      <select
                        value={m.status || "active"}
                        onChange={(e) => updateMut.mutate({ userId: m.user_id, status: e.target.value })}
                        className={`rounded-md border px-2 py-1 text-xs ${statusBadgeClass(m.status === "active" ? "approved" : m.status === "suspended" ? "blocker" : "in_review")}`}
                      >
                        <option value="active">Active</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </td>
                    <td className="sticky end-0 z-10 border-s border-gold/20 bg-card p-3">

                      <div className="flex flex-col items-end gap-1">
                        {recommendedAction && (
                          <button
                            disabled={actionMut.isPending}
                            onClick={() => {
                              if (confirm(`${recommendedAction.label} to ${m.email || m.display_name}?`)) {
                                actionMut.mutate({ userId: m.user_id, action: recommendedAction.action });
                              }
                            }}
                            className="rounded-md border border-gold/40 bg-gold/10 px-2 py-1 text-xs text-[var(--gold-deep)] hover:bg-gold/20 disabled:opacity-40 whitespace-nowrap"
                            title={`Send: ${recommendedAction.label}`}
                          >
                            {recommendedAction.label}
                          </button>
                        )}
                        {(st === "email_unverified" || m.email_verified === false) && (
                          <button
                            disabled={manualVerifyMut.isPending}
                            onClick={() => setVerifyTarget({ userId: m.user_id, email: m.email || m.display_name || "this user" })}
                            className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-800 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100 dark:hover:bg-emerald-900/60 disabled:opacity-40 whitespace-nowrap"
                            title="Manually verify this user's email address"
                          >
                            Manually Verify Email
                          </button>
                        )}
                        <div className="flex gap-1">
                          <Link
                            to="/admin/users/$userId"
                            params={{ userId: m.user_id }}
                            className="rounded-md border border-gold/20 bg-background px-2 py-1 text-xs hover:bg-gold/10"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => exportUser(m.user_id, m.display_name)}
                            className="rounded-md border border-gold/20 bg-background px-2 py-1 text-xs hover:bg-gold/10"
                            title="Export user data"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </button>
                          <button
                            disabled={m.user_id === currentUserId}
                            onClick={() => {
                              if (m.user_id === currentUserId) {
                                toast.error("You can't delete your own admin account.");
                                return;
                              }
                              if (confirm(`Delete ${m.display_name || "this account"}? This cannot be undone.`)) deleteMut.mutate(m.user_id);
                            }}
                            className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={m.user_id === currentUserId ? "You can't delete your own account" : "Delete account"}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AlertDialog open={!!verifyTarget} onOpenChange={(o) => { if (!o) setVerifyTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Manually verify email address?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to manually verify this user's email address? This
              should only be used after confirming the user's identity.
              {verifyTarget ? (
                <span className="mt-2 block font-medium text-foreground">{verifyTarget.email}</span>
              ) : null}
              <span className="mt-2 block text-xs">
                This only bypasses email verification. It does not activate a membership
                or change any payment information — the user still completes payment
                through Stripe or PayPal.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={manualVerifyMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={manualVerifyMut.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (verifyTarget) manualVerifyMut.mutate(verifyTarget.userId);
              }}
            >
              {manualVerifyMut.isPending ? "Verifying…" : "Verify email"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============= Feedback & Reports Tab =============

const FB_CATEGORIES = [
  { value: "general", label: "General Feedback" },
  { value: "bug", label: "Bug Report" },
  { value: "complaint", label: "Complaint" },
  { value: "safety", label: "Safety Concern" },
  { value: "community", label: "Community Report" },
  { value: "therapist", label: "Therapist Report" },
  { value: "feature_request", label: "Feature Request" },
] as const;
const FB_STATUSES = [
  { value: "new", label: "New" },
  { value: "under_review", label: "Under Review" },
  { value: "waiting_response", label: "Waiting for Response" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
] as const;

function FeedbackTab() {
  const fetcher = useServerFn(listFeedback);
  const creator = useServerFn(createFeedback);
  const updater = useServerFn(updateFeedback);
  const remover = useServerFn(deleteFeedback);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin", "feedback"], queryFn: () => fetcher() });
  const [showNew, setShowNew] = useState(false);
  const [filterCat, setFilterCat] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "feedback"] });

  const createMut = useMutation({
    mutationFn: (vars: any) => creator({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Report logged"); setShowNew(false); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });
  const updateMut = useMutation({
    mutationFn: (vars: any) => updater({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Saved"); },
    onError: (e: any) => toast.error(e?.message || "Save failed"),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => remover({ data: { id } }),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
  });

  const reports = (data?.reports ?? []).filter((r: any) => {
    if (filterCat !== "all" && r.category !== filterCat) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All categories</option>
          {FB_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-md border border-gold/20 bg-card px-3 py-2 text-sm">
          <option value="all">All statuses</option>
          {FB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div className="ms-auto flex items-center gap-2">
          <ExportButtons
            rows={reports as any[]}
            columns={[
              { key: "id", header: "Report ID" },
              { key: "category", header: "Category" },
              { key: "display_name", header: "User", get: (r: any) => r.display_name || (r.user_id ?? "anonymous") },
              { key: "status", header: "Status" },
              { key: "description", header: "Description" },
              { key: "assigned_to", header: "Assigned To" },
              { key: "resolution_notes", header: "Resolution" },
              { key: "screenshot_url", header: "Screenshot URL" },
              { key: "created_at", header: "Date Submitted", get: (r: any) => new Date(r.created_at).toISOString() },
            ]}
            filename="recovery-trauma-feedback-reports"
            sheetName="Feedback"
            filters={{ category: filterCat, status: filterStatus }}
          />
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white">
            <Plus className="h-4 w-4" /> Log report
          </button>
        </div>
      </div>


      {showNew && <FeedbackForm onCancel={() => setShowNew(false)} onSubmit={(v) => createMut.mutate(v)} submitting={createMut.isPending} />}

      {isLoading ? (
        <div className="text-muted-foreground">Loading reports…</div>
      ) : reports.length === 0 ? (
        <div className="rounded-2xl border border-gold/20 bg-card p-8 text-center text-muted-foreground">No reports.</div>
      ) : (
        <div className="space-y-3">
          {reports.map((r: any) => (
            <div key={r.id} className="rounded-2xl border border-gold/20 bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-mono text-muted-foreground">#{r.id.slice(0, 8)}</span>
                    <span className="text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                    <span className="rounded-md border border-gold/20 bg-background px-2 py-0.5">{FB_CATEGORIES.find((c) => c.value === r.category)?.label || r.category}</span>
                    <span className={`rounded-md border px-2 py-0.5 ${statusBadgeClass(r.status === "resolved" || r.status === "closed" ? "approved" : r.status === "new" ? "default" : "in_review")}`}>
                      {FB_STATUSES.find((s) => s.value === r.status)?.label || r.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm whitespace-pre-wrap">{r.description}</p>
                  <div className="mt-1 text-xs text-muted-foreground">
                    From: {r.display_name || (r.user_id ? r.user_id.slice(0, 8) : "anonymous")}
                  </div>
                  {r.screenshot_url && (
                    <a href={r.screenshot_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-[var(--gold-deep)] underline">
                      View screenshot
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <select
                    value={r.status}
                    onChange={(e) => updateMut.mutate({ id: r.id, status: e.target.value })}
                    className="rounded-md border border-gold/20 bg-background px-2 py-1 text-xs"
                  >
                    {FB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <button onClick={() => { if (confirm("Delete this report?")) delMut.mutate(r.id); }} className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <input
                  defaultValue={r.assigned_to ?? ""}
                  placeholder="Assigned to"
                  onBlur={(e) => { if (e.target.value !== (r.assigned_to ?? "")) updateMut.mutate({ id: r.id, assigned_to: e.target.value || null }); }}
                  className="rounded-md border border-gold/20 bg-background p-2 text-xs"
                />
                <textarea
                  defaultValue={r.resolution_notes ?? ""}
                  placeholder="Resolution notes"
                  rows={1}
                  onBlur={(e) => { if (e.target.value !== (r.resolution_notes ?? "")) updateMut.mutate({ id: r.id, resolution_notes: e.target.value || null }); }}
                  className="rounded-md border border-gold/20 bg-background p-2 text-xs"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackForm({ onCancel, onSubmit, submitting }: { onCancel: () => void; onSubmit: (v: any) => void; submitting: boolean }) {
  const [category, setCategory] = useState("general");
  const [description, setDescription] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-4 space-y-3">
      <h3 className="font-display text-lg">Log a feedback or report</h3>
      <div className="grid gap-2 sm:grid-cols-2">
        <select className="rounded-md border border-gold/20 bg-background p-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
          {FB_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <input className="rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Assigned to" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} />
      </div>
      <textarea className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Description*" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      <input className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Screenshot URL (optional)" value={screenshotUrl} onChange={(e) => setScreenshotUrl(e.target.value)} />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg border border-gold/20 px-4 py-2 text-sm">Cancel</button>
        <button
          disabled={submitting || !description.trim()}
          onClick={() => onSubmit({ category, description: description.trim(), screenshot_url: screenshotUrl || undefined, assigned_to: assignedTo || undefined })}
          className="rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Save report"}
        </button>
      </div>
    </div>
  );
}

// ============= Safety & Incidents Tab =============

const SEV_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
] as const;

function SafetyTab() {
  const fetcher = useServerFn(listIncidents);
  const creator = useServerFn(createIncident);
  const updater = useServerFn(updateIncident);
  const remover = useServerFn(deleteIncident);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin", "incidents"], queryFn: () => fetcher() });
  const [showNew, setShowNew] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "incidents"] });

  const createMut = useMutation({
    mutationFn: (vars: any) => creator({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Incident logged"); setShowNew(false); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });
  const updateMut = useMutation({
    mutationFn: (vars: any) => updater({ data: vars }),
    onSuccess: () => { invalidate(); toast.success("Saved"); },
    onError: (e: any) => toast.error(e?.message || "Save failed"),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => remover({ data: { id } }),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <ExportButtons
          rows={(data?.incidents ?? []) as any[]}
          columns={[
            { key: "incident_number", header: "Incident Number", get: (r: any) => `INC-${String(r.incident_number).padStart(4, "0")}` },
            { key: "occurred_at", header: "Date", get: (r: any) => r.occurred_at ? new Date(r.occurred_at).toISOString() : "" },
            { key: "description", header: "Description" },
            { key: "severity", header: "Severity" },
            { key: "action_taken", header: "Action Taken" },
            { key: "outcome", header: "Outcome" },
            { key: "follow_up_required", header: "Follow-Up Required", get: (r: any) => r.follow_up_required ? "yes" : "no" },
            { key: "follow_up_notes", header: "Follow-Up Notes" },
          ]}
          filename="recovery-trauma-incidents"
          sheetName="Incidents"
        />
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white">
          <Plus className="h-4 w-4" /> Log incident
        </button>
      </div>


      {showNew && <IncidentForm onCancel={() => setShowNew(false)} onSubmit={(v) => createMut.mutate(v)} submitting={createMut.isPending} />}

      {isLoading ? (
        <div className="text-muted-foreground">Loading incidents…</div>
      ) : (data?.incidents ?? []).length === 0 ? (
        <div className="rounded-2xl border border-gold/20 bg-card p-8 text-center text-muted-foreground">No incidents recorded.</div>
      ) : (
        <div className="space-y-3">
          {(data?.incidents ?? []).map((i: any) => (
            <div key={i.id} className="rounded-2xl border border-gold/20 bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-mono font-semibold text-foreground">INC-{String(i.incident_number).padStart(4, "0")}</span>
                    <span className="text-muted-foreground">{new Date(i.occurred_at).toLocaleString()}</span>
                    <span className={`rounded-md border px-2 py-0.5 ${statusBadgeClass(i.severity === "critical" || i.severity === "high" ? "blocker" : i.severity === "medium" ? "in_review" : "default")}`}>
                      {i.severity}
                    </span>
                    {i.follow_up_required && <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-800">Follow-up</span>}
                  </div>
                  <p className="mt-2 text-sm whitespace-pre-wrap">{i.description}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <select
                    value={i.severity}
                    onChange={(e) => updateMut.mutate({ id: i.id, severity: e.target.value })}
                    className="rounded-md border border-gold/20 bg-background px-2 py-1 text-xs"
                  >
                    {SEV_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <button onClick={() => { if (confirm("Delete this incident?")) delMut.mutate(i.id); }} className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">Action taken</label>
                  <textarea
                    defaultValue={i.action_taken ?? ""}
                    rows={2}
                    onBlur={(e) => { if (e.target.value !== (i.action_taken ?? "")) updateMut.mutate({ id: i.id, action_taken: e.target.value || null }); }}
                    className="mt-1 w-full rounded-md border border-gold/20 bg-background p-2 text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">Outcome</label>
                  <textarea
                    defaultValue={i.outcome ?? ""}
                    rows={2}
                    onBlur={(e) => { if (e.target.value !== (i.outcome ?? "")) updateMut.mutate({ id: i.id, outcome: e.target.value || null }); }}
                    className="mt-1 w-full rounded-md border border-gold/20 bg-background p-2 text-xs"
                  />
                </div>
              </div>
              <div className="mt-3 flex items-start gap-3">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    defaultChecked={!!i.follow_up_required}
                    onChange={(e) => updateMut.mutate({ id: i.id, follow_up_required: e.target.checked })}
                  />
                  Follow-up required
                </label>
                <textarea
                  defaultValue={i.follow_up_notes ?? ""}
                  placeholder="Follow-up notes"
                  rows={1}
                  onBlur={(e) => { if (e.target.value !== (i.follow_up_notes ?? "")) updateMut.mutate({ id: i.id, follow_up_notes: e.target.value || null }); }}
                  className="flex-1 rounded-md border border-gold/20 bg-background p-2 text-xs"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IncidentForm({ onCancel, onSubmit, submitting }: { onCancel: () => void; onSubmit: (v: any) => void; submitting: boolean }) {
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [actionTaken, setActionTaken] = useState("");
  const [outcome, setOutcome] = useState("");
  const [followUp, setFollowUp] = useState(false);
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-4 space-y-3">
      <h3 className="font-display text-lg">New incident</h3>
      <textarea className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Description*" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      <div className="grid gap-2 sm:grid-cols-2">
        <select className="rounded-md border border-gold/20 bg-background p-2 text-sm" value={severity} onChange={(e) => setSeverity(e.target.value)}>
          {SEV_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} />
          Follow-up required
        </label>
      </div>
      <textarea className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Action taken" rows={2} value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} />
      <textarea className="w-full rounded-md border border-gold/20 bg-background p-2 text-sm" placeholder="Outcome" rows={2} value={outcome} onChange={(e) => setOutcome(e.target.value)} />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg border border-gold/20 px-4 py-2 text-sm">Cancel</button>
        <button
          disabled={submitting || !description.trim()}
          onClick={() => onSubmit({ description: description.trim(), severity, action_taken: actionTaken || undefined, outcome: outcome || undefined, follow_up_required: followUp })}
          className="rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Log incident"}
        </button>
      </div>
    </div>
  );
}


function LaunchDashboardTab() {

  const fetcher = useServerFn(getLaunchDashboard);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "launch-dashboard"],
    queryFn: () => fetcher(),
    refetchInterval: 30_000,
  });

  if (isLoading) return <div className="text-muted-foreground">Loading launch dashboard…</div>;
  if (!data) return null;

  const m = data.members;
  const rev = data.revenue;
  const t = data.therapists;

  const fmt = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const money = (cents: number, currency = "USD") =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format((cents ?? 0) / 100);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="mb-3 font-display text-lg text-foreground">💛 Members</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
          <StatCard label="Free Accounts (Payment Required)" value={m.free} />
          <StatCard label="Paid Founding Members" value={m.founding} hint="Active paid founding tier" />
          <StatCard label="Active Paid Members" value={m.activePaid} />
          <StatCard label="Cancelled Memberships" value={m.cancelled} />
          <StatCard label="Refunded Memberships" value={m.refunded} />
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-display text-lg text-foreground">💳 Membership revenue</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <StatCard
            label="MRR (Members)"
            value={money(rev.memberMrrCents, rev.currency)}
            hint="Sum of active member payments"
          />
          <StatCard
            label="MRR (Therapists)"
            value={money(rev.therapistMrrCents, rev.currency)}
            hint="Recurring therapist billing (not yet live)"
          />
          <StatCard label="Total Active Subscriptions" value={rev.totalActiveSubs} />
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-display text-lg text-foreground">👩‍⚕️ Therapists</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
          <StatCard label="Active Therapists" value={t.active} hint="Live in directory" />
          <StatCard label="Pending Applications" value={t.pending} />
          <StatCard label="Awaiting Payment" value={t.awaitingPayment} />
          <StatCard label="Ready for Review" value={t.readyForReview} />
          <StatCard label="Live in Directory" value={t.liveInDirectory} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Test / demo accounts flagged as such are excluded from all therapist statistics.
        </p>
      </div>

      <div>
        <h3 className="mb-3 font-display text-lg text-foreground">👀 Website analytics</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-2">
          <StatCard label="Visitors Today" value={data.visitors?.today ?? 0} hint="unique browsers (UTC day)" />
          <StatCard label="All-time Visitors" value={data.visitors?.total ?? 0} hint="unique browser-days" />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Counts one row per browser per UTC day. Admin pages are excluded.
        </p>
      </div>

      <div>
        <h3 className="mb-3 font-display text-lg text-foreground">🔔 Recent activity</h3>
        <div className="overflow-hidden rounded-2xl border border-gold/20 bg-card">
          {data.activity.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No recent activity yet.</div>
          ) : (
            <ul className="divide-y divide-gold/10">
              {data.activity.map((a, i) => (
                <li key={`${a.kind}-${i}-${a.when}`} className="flex items-center gap-3 px-4 py-3 text-sm">
                  <span
                    className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs ${
                      a.kind === "membership"
                        ? "bg-gold/15 text-[var(--gold-deep)]"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                    title={a.kind}
                  >
                    {a.kind === "membership" ? "💛" : "👩‍⚕️"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium text-foreground">{a.title}</div>
                    <div className="truncate text-xs text-muted-foreground">{a.subtitle}</div>
                  </div>
                  <div className="shrink-0 text-xs text-muted-foreground">{fmt(a.when)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Live reconciliation of the database against Stripe and PayPal.
 * "Stripe + PayPal" is only ever shown when both providers report a live
 * subscription (i.e. the member really is being billed twice).
 */
function ProviderAuditPanel() {
  const runAudit = useServerFn(auditPaymentProviders);
  const [data, setData] = useState<Awaited<ReturnType<typeof auditPaymentProviders>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setErr(null);
    try {
      setData(await runAudit({ data: undefined as never }));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const c = data?.counts;
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Payment provider audit (live Stripe + PayPal)</p>
          <p className="text-xs text-muted-foreground">
            Verifies every stored subscription against the providers.
            {data ? ` Last checked ${new Date(data.checked_at).toLocaleString()}.` : ""}
          </p>
        </div>
        <button
          onClick={run}
          disabled={busy}
          className="rounded-full border border-gold/30 bg-background px-3 py-1.5 text-xs disabled:opacity-50"
        >
          {busy ? "Checking providers…" : "Run audit"}
        </button>
      </div>

      {err && <p className="mt-3 text-xs text-red-700">{err}</p>}
      {data?.errors?.length ? (
        <p className="mt-3 text-xs text-amber-800">Provider errors: {data.errors.join(" · ")}</p>
      ) : null}

      {c && (
        <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3 lg:grid-cols-6">
          {[
            ["Live Stripe subs (Stripe API)", c.stripe_live_total],
            ["Live Stripe matched to members", c.stripe_live_matched],
            ["Live PayPal subs (PayPal API)", c.paypal_live],
            ["PayPal rows stored", c.paypal_stored],
            ["Stripe (historical PayPal)", c.stripe_historical_paypal],
            ["PayPal (historical Stripe)", c.paypal_historical_stripe],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-xl border border-gold/15 bg-background p-2">
              <p className="text-[11px] text-muted-foreground">{label}</p>
              <p className="text-lg font-semibold">{value as number}</p>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div className="mt-3">
          {data.counts.both_live === 0 ? (
            <p className="text-xs text-emerald-800">
              No member is billed by both providers.
            </p>
          ) : (
            <div>
              <p className="mb-1 text-xs font-medium text-red-800">
                {data.counts.both_live} member(s) have a live subscription at BOTH providers — double billing:
              </p>
              <ul className="space-y-1 text-xs">
                {data.dual_billed.map((r) => (
                  <li key={r.user_id} className="rounded-lg border border-red-200 bg-red-50 p-2">
                    {r.email ?? r.user_id} — Stripe <code>{r.stripe_subscription_id}</code> ({r.stripe_live_status})
                    {" · "}PayPal <code>{r.paypal_subscription_id}</code> ({r.paypal_live_status})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
